<template>
  <div class="agent-root">
    <div class="app no-sidebar">
      <!-- ============ CONVERSATION ============ -->
      <div v-if="dossierData" class="conversation">
        <header class="dossier-header">
          <div class="dh-left">
            <button class="icon-btn" @click="goDashboard" :title="$t('common.back')">
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style="transform:rotate(180deg)"><path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <div>
              <div class="dh-kicker">{{ $t('agent.header.dossierKicker') }}</div>
              <div class="dh-title">{{ dossierName || $t('agent.header.unnamed') }}</div>
            </div>
          </div>
          <div class="dh-right">
            <button class="btn btn-primary" @click="goDashboard">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
              {{ $t('agent.header.newSearch') }}
            </button>
          </div>
        </header>

        <div class="thread" ref="thread">
          <div class="thread-inner">
            <template v-for="msg in chatMessages">
              <!-- User message -->
              <div v-if="msg.role === 'user'" :key="msg.id" class="msg msg-user">
                <div class="msg-bubble">
                  <div class="msg-text">{{ msg.text }}</div>
                </div>
              </div>

              <!-- Agent message -->
              <div v-else :key="msg.id" class="msg msg-agent">
                <div class="msg-meta">
                  <div class="a-avatar">
                    <img :src="faviconJ" alt="" />
                  </div>
                  <span>{{ $t('agent.message.agentAt', { at: msg.at }) }}</span>
                </div>

                <div class="agent-body">
                  <!-- Live Timeline -->
                  <div :class="['live-timeline', { streaming: msg.status === 'streaming' }]">
                    <button class="lt-header" @click="toggleTimeline(msg.id)">
                      <div class="lt-left">
                        <span v-if="msg.status === 'streaming'" class="agent-badge streaming">
                          <span class="pulse-dot"></span> {{ $t('agent.timeline.inProgress') }}
                        </span>
                        <span v-else-if="msg.status === 'failed'" class="agent-badge failed">
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 1L14.93 13H1.07L8 1z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M8 6v3M8 11v1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                          Échec
                        </span>
                        <span v-else class="agent-badge done">
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8l3 3 7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                          {{ $t('agent.timeline.reasoningTitle') }}
                        </span>
                        <span class="lt-meta">
                          <template v-if="msg.status === 'streaming'">
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5"/><path d="M8 5v3l2 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                            {{ msg.elapsed || '…' }}
                          </template>
                          <template v-else>{{ $tc('agent.timeline.turnCount', msg.iterations || 1, { n: msg.iterations || 1 }) }} · {{ $tc('agent.timeline.stepCount', msg.timeline.length, { n: msg.timeline.length }) }}</template>
                        </span>
                      </div>
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" :style="{ transform: isTimelineExpanded(msg.id) ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform .15s' }"><path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    </button>
                    <div v-if="isTimelineExpanded(msg.id)" class="lt-body">
                      <div
                        v-for="(step, i) in msg.timeline"
                        :key="i"
                        :class="['tl-step', 'tl-step-' + step.type, { 'tl-active': msg.status === 'streaming' && i === msg.timeline.length - 1 }]"
                      >
                        <div class="tl-rail">
                          <div :class="['tl-bubble', tlBubbleClass(step.type), { pulsing: msg.status === 'streaming' && i === msg.timeline.length - 1 }]">
                            <template v-if="step.type === 'reasoning'">R</template>
                            <svg v-else-if="step.type === 'search'" width="12" height="12" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5" stroke="currentColor" stroke-width="1.5"/><path d="M11 11L14 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                            <svg v-else-if="step.type === 'result'" width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3 8l3 3 7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            <svg v-else-if="step.type === 'doc'" width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3 2h6l4 4v8a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M9 2v4h4" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>
                            <svg v-else-if="step.type === 'doc_result'" width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3 8l3 3 7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            <svg v-else-if="step.type === 'workspace'" width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M2 4a1 1 0 011-1h3l1 1h6a1 1 0 011 1v7a1 1 0 01-1 1H3a1 1 0 01-1-1V4z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>
                            <svg v-else-if="step.type === 'workspace_result'" width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3 8l3 3 7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            <svg v-else-if="step.type === 'web'" width="12" height="12" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5"/><path d="M2 8h12M8 2c2 2 2 10 0 12M8 2c-2 2-2 10 0 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                            <svg v-else-if="step.type === 'web_result'" width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3 8l3 3 7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            <svg v-else-if="step.type === 'parliament'" width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M2 13h12M3 13V7M6 13V7M10 13V7M13 13V7M2 7l6-5 6 5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            <svg v-else-if="step.type === 'parliament_result'" width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3 8l3 3 7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            <svg v-else-if="step.type === 'answering'" width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M8 1v4M8 11v4M1 8h4M11 8h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                          </div>
                          <div v-if="i < msg.timeline.length - 1" class="tl-line"></div>
                        </div>
                        <div class="tl-body-text">
                          <template v-if="step.type === 'reasoning'">
                            <div class="tl-head">
                              <strong>{{ $t('agent.timeline.steps.reasoning') }}</strong>
                              <span v-if="step.iter" class="tl-iter">{{ $t('agent.timeline.turn', { n: step.iter }) }}</span>
                            </div>
                            <div class="tl-text tl-reasoning-content" v-html="renderReasoning(step.content)"></div>
                          </template>
                          <template v-else-if="step.type === 'search'">
                            <div class="tl-head">
                              <strong>{{ $t('agent.timeline.steps.search') }}</strong>
                              <span v-if="step.iter" class="tl-iter">{{ $t('agent.timeline.turn', { n: step.iter }) }}</span>
                            </div>
                            <div class="tl-text tl-mono" v-if="step.instruction">{{ $t('agent.timeline.instructionQuoted', { instruction: step.instruction }) }}</div>
                          </template>
                          <template v-else-if="step.type === 'result'">
                            <div class="tl-head tl-head-success">
                              <strong>{{ $tc('agent.timeline.docsFound', step.nb * 5, { n: step.nb * 5 }) }}</strong>
                            </div>
                            <div class="tl-text" v-if="step.sourcesUsed && step.sourcesUsed.length">
                              {{ $t('agent.timeline.sourcesLabel', { list: step.sourcesUsed.join(', ') }) }}
                            </div>
                          </template>
                          <template v-else-if="step.type === 'doc'">
                            <div class="tl-head">
                              <strong>{{ $t('agent.timeline.steps.doc') }}</strong>
                              <span v-if="step.iter" class="tl-iter">{{ $t('agent.timeline.turn', { n: step.iter }) }}</span>
                            </div>
                            <div class="tl-text tl-doc-ref" v-if="step.ref">{{ step.ref }}</div>
                          </template>
                          <template v-else-if="step.type === 'doc_result'">
                            <div class="tl-head tl-head-success">
                              <strong>{{ step.title }}</strong>
                            </div>
                            <div class="tl-text tl-error" v-if="step.error">{{ step.error }}</div>
                          </template>
                          <template v-else-if="step.type === 'workspace'">
                            <div class="tl-head">
                              <strong>{{ workspaceName ? `Recherche dans le workspace « ${workspaceName} »` : 'Recherche dans le workspace' }}</strong>
                              <span v-if="step.iter" class="tl-iter">{{ $t('agent.timeline.turn', { n: step.iter }) }}</span>
                            </div>
                            <div class="tl-text tl-mono" v-if="step.question">« {{ step.question }} »</div>
                          </template>
                          <template v-else-if="step.type === 'workspace_result'">
                            <div class="tl-head tl-head-success">
                              <strong>{{ workspaceName ? `Réponse du workspace « ${workspaceName} »` : 'Réponse du workspace' }}</strong>
                            </div>
                            <div class="tl-text" v-if="step.answerPreview">{{ step.answerPreview }}<span v-if="step.answerPreview.length >= 500">…</span></div>
                            <div class="tl-text tl-error" v-if="step.error">{{ step.error }}</div>
                          </template>
                          <template v-else-if="step.type === 'web'">
                            <div class="tl-head">
                              <strong>Recherche web</strong>
                              <span v-if="step.iter" class="tl-iter">{{ $t('agent.timeline.turn', { n: step.iter }) }}</span>
                            </div>
                            <div class="tl-text tl-mono" v-if="step.instruction">« {{ step.instruction }} »</div>
                          </template>
                          <template v-else-if="step.type === 'web_result'">
                            <div class="tl-head tl-head-success">
                              <strong>Résultats web</strong>
                            </div>
                            <div class="tl-text" v-if="step.answerPreview">{{ step.answerPreview }}<span v-if="step.answerPreview.length >= 500">…</span></div>
                            <div class="tl-text tl-error" v-if="step.error">{{ step.error }}</div>
                          </template>
                          <template v-else-if="step.type === 'parliament'">
                            <div class="tl-head">
                              <strong>Recherche parlementaire</strong>
                              <span v-if="step.iter" class="tl-iter">{{ $t('agent.timeline.turn', { n: step.iter }) }}</span>
                            </div>
                            <div class="tl-text tl-mono" v-if="step.instruction">« {{ step.instruction }} »</div>
                          </template>
                          <template v-else-if="step.type === 'parliament_result'">
                            <div class="tl-head tl-head-success">
                              <strong>Recherche parlementaire terminée</strong>
                            </div>
                            <!-- Réponse intermédiaire du parl agent volontairement masquée :
                                 elle contient des marqueurs bruts ([parl:N]) et fait doublon
                                 avec la synthèse finale. On ne montre que l'erreur si échec. -->
                            <div class="tl-text tl-error" v-if="step.error">{{ step.error }}</div>
                          </template>
                          <template v-else-if="step.type === 'answering'">
                            <div class="tl-head">
                              <strong>{{ $t('agent.timeline.steps.answering') }}</strong>
                            </div>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Synthese (if done) -->
                  <template v-if="msg.status === 'done' && msg.syntheseHtml">
                    <div class="synthese">
                      <div
                        class="synthese-body"
                        v-html="msg.syntheseHtml"
                        @click="onSyntheseClick($event)"
                      ></div>
                    </div>

                    <div class="msg-actions-row">
                      <button class="btn-ghost-sm" @click="copySynthese(msg)">
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="5" y="5" width="9" height="9" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M11 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v7a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5"/></svg>
                        {{ $t('common.copy') }}
                      </button>
                      <button
                        class="btn-word-sm"
                        :disabled="exportingTurnIdx === msg.turnIdx"
                        @click="exportTurnWord(msg)"
                      >
                        <svg v-if="exportingTurnIdx === msg.turnIdx" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" class="spinner-icon" viewBox="0 0 16 16">
                          <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z"/>
                          <path fill-rule="evenodd" d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5.002 5.002 0 0 0 8 3zM3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9H3.1z"/>
                        </svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M5.485 6.879a.5.5 0 1 0-.97.242l1.5 6a.5.5 0 0 0 .967.01L8 9.402l1.018 3.73a.5.5 0 0 0 .967-.01l1.5-6a.5.5 0 0 0-.97-.242l-1.036 4.144-.997-3.655a.5.5 0 0 0-.964 0l-.997 3.655L5.485 6.88z"/>
                          <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
                        </svg>
                        {{ exportingTurnIdx === msg.turnIdx ? $t('agent.header.downloading') : $t('agent.header.word') }}
                      </button>
                      <button
                        class="btn-memo-sm"
                        @click="showTransformModal = true"
                        :title="$t('dossier.tooltips.transformToMemo')"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M5 4a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1H5zm-.5 2.5A.5.5 0 0 1 5 6h6a.5.5 0 0 1 0 1H5a.5.5 0 0 1-.5-.5zM5 8a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1H5zm0 2a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1H5z"/>
                          <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2zm10-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1z"/>
                        </svg>
                        {{ $t('dossier.transform.memo') }}
                      </button>
                    </div>

                    <!-- Feedback utilisateur (par tour) -->
                    <FeedbackModal
                      :isVisible="true"
                      :dossierId="currentDossierId"
                      :dossierMode="dossierData && dossierData.mode ? dossierData.mode : 'AGENT'"
                      :turnIdx="msg.turnIdx"
                    />
                  </template>

                  <!-- Streaming skeleton (when no timeline yet) -->
                  <div v-else-if="msg.status === 'streaming' && !msg.timeline.length" class="streaming-preview">
                    <div class="sp-grid">
                      <div v-for="i in 3" :key="i" class="sp-card">
                        <div class="sp-card-line w80"></div>
                        <div class="sp-card-line w60"></div>
                        <div class="sp-card-line w40"></div>
                      </div>
                    </div>
                    <div class="sp-hint">{{ $t('agent.streamingPreview') }}</div>
                  </div>

                  <!-- Failed state -->
                  <div v-else-if="msg.status === 'failed'" class="agent-failed-block">
                    <div v-if="msg.syntheseHtml" v-html="msg.syntheseHtml" class="agent-failed-detail"></div>
                    <p v-else class="agent-failed-msg">L'analyse n'a pas pu aboutir. Vous pouvez réessayer ci-dessous.</p>
                    <button
                      v-if="msg.id === 'a-init'"
                      class="btn-retry"
                      :disabled="isRetrying"
                      @click="retryDossier"
                    >
                      <svg v-if="isRetrying" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" class="spinner-icon" viewBox="0 0 16 16">
                        <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z"/>
                        <path fill-rule="evenodd" d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5.002 5.002 0 0 0 8 3zM3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9H3.1z"/>
                      </svg>
                      <svg v-else width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M13.5 8A5.5 5.5 0 1 1 8 2.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M8 1v4l2.5-2L8 1z" fill="currentColor"/></svg>
                      {{ isRetrying ? 'Relance…' : 'Réessayer' }}
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- Composer (style: TransformModal search bar) -->
        <div class="composer">
          <!-- Panneau document (mode rédaction) : dernier docx produit par
               l'agent. Pas de rendu riche côté front (le GET renvoie le docx
               brut en b64) : nom du fichier + téléchargement. -->
          <div v-if="redactionPanelVisible" class="redaction-doc-panel">
            <div class="rdp-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 2h6l4 4v8a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M9 2v4h4" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>
            </div>
            <div class="rdp-info">
              <div class="rdp-name" :title="redactionFileName">{{ redactionFileName }}</div>
              <div class="rdp-sub" :title="redactionPlanLine || ''">
                <template v-if="isFetchingRedactionDoc">Chargement du document…</template>
                <template v-else-if="redactionPlanLine">{{ redactionPlanLine }}</template>
                <template v-else>Document rédigé par l'agent</template>
              </div>
            </div>
            <button
              class="btn-word-sm rdp-download"
              :disabled="!redactionFileB64"
              :title="redactionFileB64 ? 'Télécharger le document (.docx)' : 'Document indisponible'"
              @click="downloadRedactionDocument"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                <path d="M5.485 6.879a.5.5 0 1 0-.97.242l1.5 6a.5.5 0 0 0 .967.01L8 9.402l1.018 3.73a.5.5 0 0 0 .967-.01l1.5-6a.5.5 0 0 0-.97-.242l-1.036 4.144-.997-3.655a.5.5 0 0 0-.964 0l-.997 3.655L5.485 6.88z"/>
                <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
              </svg>
              Télécharger le document (.docx)
            </button>
          </div>
          <div class="transform-search-bar" :class="{ 'transform-search-bar-multiline': composerValue.includes('\n') }">
            <textarea
              ref="composerTa"
              v-model="composerValue"
              class="transform-search-input"
              :placeholder="$t('agent.input.placeholder')"
              rows="1"
              :disabled="isFollowUpPending"
              @keydown.enter.exact.prevent="sendFollowUp"
              @input="autoResizeComposer"
            ></textarea>
            <button
              type="button"
              class="transform-send-circle"
              :disabled="!composerValue.trim() || isFollowUpPending"
              @click.stop="sendFollowUp"
              :title="$t('common.send')"
            >
              <svg v-if="isFollowUpPending" xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" class="spinner-icon" viewBox="0 0 16 16">
                <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z"/>
              </svg>
              <svg v-else class="arrow-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 19V5M5 12l7-7 7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Loading placeholder -->
      <div v-else-if="loading" class="conversation">
        <div class="loading-screen">{{ $t('agent.loading') }}</div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="conversation">
        <div class="loading-screen error">{{ error }}</div>
      </div>

    </div>

    <!-- ============ DOCUMENT PANEL (classic centered modal) ============ -->
    <ArticleDetailPanel
      :isVisible="showArticleDetailPanel"
      :articleData="articleDetailData"
      @close="closeArticleDetailPanel"
    />

    <!-- ============ Workspace citation preview ============ -->
    <CitationPreviewModal
      v-if="showCitationModal && citationModalData"
      :citation="citationModalData"
      @close="showCitationModal = false"
    />

    <!-- ============ Transformation mémo (contexte = toute la conversation) ============ -->
    <TransformModal
      :isVisible="showTransformModal"
      mode="memo"
      :synthesisText="conversationTextForTransform"
      :dossierId="currentDossierId"
      @close="showTransformModal = false"
    />

    <!-- ============ Paywall (accès coupé/expiré) ============ -->
    <SubscriptionModal
      :show="showPaywall"
      @close="closePaywallModal"
      @select-plan="upgrade"
      @navigate-to-trial="navigateToTrial"
      @navigate-to-contact="navigateToContact"
      @navigate-to-student="navigateToStudent"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { supabase } from '@/supabase/supabaseClient';
import { getCurrentUser } from '@/supabase/auth';
import ArticleDetailPanel from './ArticleDetailPanel.vue';
import CitationPreviewModal from './CitationPreviewModal.vue';
import FeedbackModal from './FeedbackModal.vue';
import SubscriptionModal from './SubscriptionModal.vue';
import TransformModal from './TransformModal.vue';
import { getSubscription } from '@/services/api';
import { buildArticleDetailData } from '@/utils/documentFormatters';
import faviconJ from '@/assets/favicon-j.png';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  Table,
  TableRow,
  TableCell,
  WidthType,
} from 'docx';
import { saveAs } from 'file-saver';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

const SOURCE_LABELS = {
  legiarti: { short: 'Legiarti', family: 'legal', long: 'Codes & lois' },
  juritext: { short: 'Juritext', family: 'juris', long: 'Jurisprudence FR' },
  euritext: { short: 'Euritext', family: 'juris', long: 'Jurisprudence UE' },
  bofip: { short: 'BOFiP', family: 'doctrine', long: 'Doctrine fiscale' },
  cnn: { short: 'CNN', family: 'convent', long: 'Conv. nationales' },
  convbi: { short: 'ConvBI', family: 'convent', long: 'Conventions coll.' },
  balo: { short: 'BALO', family: 'entreprise', long: 'BALO' },
  bodacc: { short: 'BODACC', family: 'entreprise', long: 'BODACC' },
  statuts: { short: 'Statuts', family: 'entreprise', long: 'Statuts' },
  actes_societes: { short: 'Actes soc.', family: 'entreprise', long: 'Actes sociétés' },
  inpi: { short: 'INPI', family: 'entreprise', long: 'INPI' },
  cnil: { short: 'CNIL', family: 'regulator', long: 'CNIL' },
  amf: { short: 'AMF', family: 'regulator', long: 'AMF' },
  acpr: { short: 'ACPR', family: 'regulator', long: 'ACPR' },
  concurrence: { short: 'Concurrence', family: 'regulator', long: 'Autorité concurrence' },
  eccomp: { short: 'EC Comp', family: 'regulator', long: 'Commission Européenne - Concurrence' },
  reglements: { short: 'Règl. UE', family: 'legal', long: 'Règlements UE' },
  boss: { short: 'BOSS', family: 'doctrine', long: 'BOSS (sécu soc.)' },
  infofi: { short: 'InfoFi', family: 'entreprise', long: 'Infos financières' },
};

const SOURCE_COLUMN_MAP = {
  legiarti: 'legiarti_docs', juritext: 'juritext_docs', bofip: 'bofip_docs',
  euritext: 'euritext_docs', cnn: 'cnn_docs', balo: 'balo_docs',
  statuts: 'statuts_docs', convbi: 'convbi_docs', infofi: 'infofi_docs',
  reglements: 'reglements_docs', bodacc: 'bodacc_docs',
  actes_societes: 'actes_societes_docs', concurrence: 'concurrence_docs',
  inpi: 'inpi_docs', cnil: 'cnil_docs', boss: 'boss_docs',
  acpr: 'acpr_docs', amf: 'amf_docs', eccomp: 'eccomp_docs',
  upc: 'upc_docs',
  edpb: 'edpb_docs',
  agcm: 'agcm_docs',
  giustizia_amministrativa: 'giustizia_amministrativa_docs',
  jurisprudence_europe_italy: 'jurisprudence_europe_italy_docs',
  eurlex_italy: 'eurlex_italy_docs',
};

// Canonical list of reference types (same as DossierPage) — single source of truth for the regex
const REF_TYPES = 'Jurisprudence|Legiarti|Bofip|BOFiP|BOFIP|EuritextITA|EurLexITA|Euritext|CNN|BALO|Statuts|Convbi|Infofi|Infofin|Reglements|Règlement|Règlements|Reglement|BODACC|Energy|ActesSocietes|Concurrence|INPI|Mention|CNIL|BOSS|ACPR|AMF|CompCase|UPC|EDPB|AGCM|GiustiziaAmm';

export default {
  name: 'AgentPage',

  components: {
    ArticleDetailPanel,
    CitationPreviewModal,
    FeedbackModal,
    SubscriptionModal,
    TransformModal,
  },

  data() {
    return {
      // Abonnement / paywall (même logique que dashboardPage)
      sub: { plan: "none", status: "none", queries_used: 0, current_period_end: null },
      showPaywall: false,

      // Core dossier state
      dossierData: null,
      dossierName: '',
      isAnalysing: null,
      analysesFiscales: [],
      loading: true,
      error: null,
      pollingInterval: null,

      // User
      userEmail: '',
      userInitial: 'U',

      // UI state
      docPane: null, // { source, n }
      showArticleDetailPanel: false,
      articleDetailData: {
        title: '',
        content: '',
        source: '',
        sourceData: null,
      },
      expandedTimelineMap: {}, // { msgId: bool }
      openSourcesMap: {}, // { msgId: bool }
      activeSourceMap: {}, // { msgId: sourceKey }

      // Composer / follow-up chained agent
      composerValue: '',
      followUpMessages: [],         // optimistic pending Q/A pendant que l'agent travaille
      isFollowUpPending: false,
      pendingFollowUpId: null,

      // Word export — per-turn (turnIdx en cours d'export, ou null)
      exportingTurnIdx: null,

      // Transformation mémo (contexte = toute la conversation)
      showTransformModal: false,

      // Retry initial analysis
      isRetrying: false,

      // Workspace citations
      workspaceCitationsMap: {},
      showCitationModal: false,
      citationModalData: null,
      workspaceName: '',

      // Mode rédaction (dossier agent lancé avec un template_id) : dernier
      // document produit, via GET /redacteur/api/v1/dossiers/<id>/document
      redactionDoc: null,          // {document_id, file_name, file, doc_state, template_id}
      redactionTemplateId: null,   // renvoyé par le GET ; repart dans les follow-ups
      isFetchingRedactionDoc: false,

      faviconJ,
    };
  },

  computed: {
    currentDossierId() {
      return this.$route.params.dossierId;
    },

    // Accès : réplique de dashboardPage.vue (canRequest). Gate le follow-up agent.
    quotaRemaining() {
      if (this.sub.plan === "student") return 25 - this.sub.queries_used;
      if (this.sub.plan === "premium") return 100;
      return 100;
    },

    canRequest() {
      if (this.sub.plan === "lifetime_free") return true;
      const active = this.sub.status === "active" || this.sub.status === "trialing";
      const paying = ["student", "premium", "trialing"].includes(this.sub.plan);

      let periodValid = true;
      if (this.sub.current_period_end) {
        const periodEnd = new Date(this.sub.current_period_end);
        const now = new Date();
        periodValid = periodEnd > now;
      }

      const underQueryLimit = this.sub.queries_used < 100000;

      return active && paying && periodValid && this.quotaRemaining > 0 && underQueryLimit;
    },

    analysesBySource() {
      // Back-compat : map source → docs du PREMIER turn (utilisé par firstAgentMessage).
      // Pour chaque turn dans chatMessages on recalcule via getSourcesMapForPoint.
      return this.getSourcesMapForPoint(this.analysesFiscales[0]);
    },

    parsedSynthese() {
      if (!this.dossierData || !this.dossierData.synthese) return null;
      const raw = this.dossierData.synthese;
      if (typeof raw !== 'string') return raw;
      try { return JSON.parse(raw); } catch (e) { return null; }
    },

    syntheseTurns() {
      // Retourne la liste des turns persistés (Q/A successifs).
      // Backward compat : si pas de turns[] (ancien format), on synthétise un seul
      // turn depuis dossierData.contexte + synthese.answer.
      const synth = this.parsedSynthese;
      if (synth && Array.isArray(synth.turns) && synth.turns.length > 0) return synth.turns;
      if (!this.dossierData) return [];
      const ans = synth?.answer || '';
      if (!ans) return [];
      return [{
        question: this.dossierData.contexte || '',
        answer: ans,
        timeline: synth?.timeline || [],
        iterations: synth?.iterations || null,
        ts: this.dossierData.created_at,
      }];
    },

    firstAgentMessage() {
      // Conservé pour compat ascendante avec d'éventuels usages externes ;
      // dans le rendu, on s'appuie désormais sur chatMessages dérivé de turns.
      const turns = this.syntheseTurns;
      if (!this.dossierData || turns.length === 0) return null;
      const t0 = turns[0];
      const status = this.isAnalysing === 'done' ? 'done'
        : (this.isAnalysing === 'failed' ? 'failed' : 'streaming');
      const sourceKeys = Object.keys(this.analysesBySource).filter(k => this.analysesBySource[k].length > 0);
      const totalDocs = sourceKeys.reduce((s, k) => s + this.analysesBySource[k].length, 0);
      return {
        id: 'a-turn-0',
        role: 'agent',
        status,
        at: this.formatTime(this.dossierData.created_at),
        timeline: (t0.timeline || []).map(s => this.mapBackendStep(s)).filter(Boolean),
        iterations: t0.iterations,
        syntheseHtml: status === 'done' ? this.renderSyntheseHtml(t0.answer || '') : null,
        sources: this.analysesBySource,
        sourceKeys,
        totalDocs,
      };
    },

    chatMessages() {
      const messages = [];
      if (!this.dossierData) {
        messages.push(...this.followUpMessages);
        return messages;
      }
      const turns = this.syntheseTurns;

      turns.forEach((t, idx) => {
        // Sources propres au turn (chaque turn a son propre analysis_point avec sa
        // propre numérotation (Legiarti - N, Bofip - N, ...)).
        const turnSources = this.getSourcesMapForPoint(this.analysesFiscales[idx]);
        const turnSourceKeys = Object.keys(turnSources).filter(k => turnSources[k].length > 0);
        const turnTotalDocs = turnSourceKeys.reduce((s, k) => s + turnSources[k].length, 0);

        // User question — pour le tout premier turn, on préfère contexte original
        const userText = idx === 0
          ? (this.dossierData.contexte || t.question || '')
          : (t.question || '');
        messages.push({
          id: `u-turn-${idx}`,
          role: 'user',
          text: userText,
          at: this.formatTime(t.ts || this.dossierData.created_at),
        });
        // Agent answer
        messages.push({
          id: `a-turn-${idx}`,
          role: 'agent',
          status: 'done',
          turnIdx: idx,
          at: this.formatTime(t.ts || this.dossierData.updated_at),
          timeline: (t.timeline || []).map(s => this.mapBackendStep(s)).filter(Boolean),
          iterations: t.iterations,
          syntheseHtml: this.renderSyntheseHtml(t.answer || '', idx),
          sources: turnSources,
          sourceKeys: turnSourceKeys,
          totalDocs: turnTotalDocs,
        });
      });

      // Si l'analyse initiale est en cours et qu'il n'y a aucun turn encore,
      // afficher la question + un placeholder agent "streaming" comme avant.
      if (turns.length === 0 && this.dossierData.contexte) {
        messages.push({
          id: 'u-init',
          role: 'user',
          text: this.dossierData.contexte,
          at: this.formatTime(this.dossierData.created_at),
        });
        const status = this.isAnalysing === 'failed' ? 'failed' : 'streaming';
        messages.push({
          id: 'a-init',
          role: 'agent',
          status,
          at: this.formatTime(this.dossierData.created_at),
          timeline: this.buildTimeline(),
          iterations: null,
          syntheseHtml: null,
          sources: {},
          sourceKeys: [],
          totalDocs: 0,
        });
      }

      // Optimistic follow-up messages : on enrichit la timeline avec la
      // progression réelle quand l'agent tourne (synthese.status === 'running')
      // — sinon le placeholder semble figé pendant 2-5 min de query_workspace.
      const liveTimeline = (this.parsedSynthese && this.parsedSynthese.status === 'running')
        ? this.buildTimeline()
        : null;
      this.followUpMessages.forEach((m) => {
        if (m.pending && liveTimeline && liveTimeline.length) {
          messages.push({ ...m, timeline: liveTimeline });
        } else {
          messages.push(m);
        }
      });
      return messages;
    },

    // Toute la conversation (Q/R de chaque turn) en texte brut, pour le
    // TransformModal (mémo). On part du HTML rendu plutôt que du markdown
    // brut : renderSyntheseHtml résout déjà les références (Legiarti - N,
    // citations naturelles, [doc:N]) en titres lisibles.
    conversationTextForTransform() {
      const turns = this.syntheseTurns;
      if (!turns.length) return '';
      const parts = [];
      turns.forEach((t, idx) => {
        const question = idx === 0
          ? (this.dossierData?.contexte || t.question || '')
          : (t.question || '');
        const answerText = this.htmlToPlainText(this.renderSyntheseHtml(t.answer || '', idx));
        if (!question && !answerText) return;
        const label = turns.length > 1 ? ` ${idx + 1}` : '';
        parts.push(`QUESTION${label} :\n${question}\n\nRÉPONSE${label} :\n${answerText}`);
      });
      return parts.join('\n\n---\n\n');
    },

    // ============ MODE RÉDACTION ============
    // Un dossier est en mode rédaction si la synthèse (racine ou dernier turn)
    // porte redaction_document {document_id, file_name}, posé par la CF.
    redactionDocumentMeta() {
      const synth = this.parsedSynthese;
      if (!synth) return null;
      if (synth.redaction_document) return synth.redaction_document;
      const turns = Array.isArray(synth.turns) ? synth.turns : [];
      for (let i = turns.length - 1; i >= 0; i--) {
        if (turns[i] && turns[i].redaction_document) return turns[i].redaction_document;
      }
      return null;
    },

    redactionPanelVisible() {
      return !!this.redactionDocumentMeta;
    },

    redactionFileName() {
      return (this.redactionDoc && this.redactionDoc.file_name)
        || (this.redactionDocumentMeta && this.redactionDocumentMeta.file_name)
        || 'document.docx';
    },

    redactionFileB64() {
      const file = this.redactionDoc && this.redactionDoc.file;
      return (file && file.data) || null;
    },

    // Ligne d'info sous le nom du fichier : plan de rédaction (titres des
    // parties) quand le doc_state renvoyé par le GET en expose un.
    redactionPlanLine() {
      const state = this.redactionDoc && this.redactionDoc.doc_state;
      const plan = state && state.plan;
      const parts = plan && Array.isArray(plan.parts) ? plan.parts : null;
      if (!parts || !parts.length) return '';
      const titles = parts.map(p => (p && p.titre) || '').filter(Boolean);
      const label = parts.length > 1 ? `${parts.length} parties` : '1 partie';
      return titles.length ? `${label} : ${titles.join(' · ')}` : label;
    },

  },

  watch: {
    '$route.params.dossierId'(newId, oldId) {
      if (newId && newId !== oldId) {
        this.stopPolling();
        this.followUpMessages = [];
        this.closeArticleDetailPanel();
        this.expandedTimelineMap = {};
        this.openSourcesMap = {};
        this.redactionDoc = null;
        this.redactionTemplateId = null;
        this.fetchDossierData();
      }
    },

    isAnalysing(newVal) {
      // Auto-expand timeline while streaming, collapse when done
      if (this.firstAgentMessage) {
        this.$set(this.expandedTimelineMap, 'a-main', newVal !== 'done');
      }
    },
  },

  mounted() {
    this.loadUser();
    this.refreshSubscription();
    this.fetchDossierData();
  },

  beforeDestroy() {
    this.stopPolling();
  },

  methods: {
    // ============ ABONNEMENT / PAYWALL ============
    async refreshSubscription() {
      try {
        this.sub = await getSubscription();
      } catch (err) {
        console.error('❌ Error fetching subscription:', err);
        this.sub = { plan: "none", status: "none", queries_used: 0, current_period_end: null };
      }
    },

    upgrade() {
      // Self-checkout retiré : la conversion passe désormais par un conseiller (sales-led).
      // On redirige vers la page de contact au lieu d'ouvrir Stripe Checkout.
      this.navigateToContact();
    },

    closePaywallModal() {
      this.showPaywall = false;
    },

    navigateToTrial() {
      this.showPaywall = false;
      this.$router.push('/trialPage');
    },

    navigateToStudent() {
      this.showPaywall = false;
      this.$router.push('/studentPage');
    },

    navigateToContact() {
      this.showPaywall = false;
      this.$router.push('/contactPage');
    },

    // ============ DATA FETCH ============
    async fetchDossierData() {
      try {
        this.loading = true;
        const user = await getCurrentUser();
        if (!user) {
          this.error = this.$t('agent.errors.mustConnect');
          this.loading = false;
          return;
        }

        const dossierId = this.currentDossierId;
        if (!dossierId) {
          this.error = this.$t('agent.errors.missingId');
          this.loading = false;
          return;
        }

        const { data: dossierData, error } = await supabase
          .from('dossiers')
          .select('*')
          .eq('id', dossierId)
          .eq('user_id', user.id)
          .single();

        if (error) {
          this.error = this.$t('agent.errors.notFound');
          this.loading = false;
          return;
        }

        this.dossierData = dossierData;
        this.dossierName = dossierData.name || this.$t('agent.header.unnamedFull');
        this.isAnalysing = dossierData.is_analysing;

        if (dossierData.workspace_id) {
          try {
            const { data: ws } = await supabase
              .from('workspaces')
              .select('name')
              .eq('id', dossierData.workspace_id)
              .single();
            this.workspaceName = ws?.name || '';
          } catch (e) { /* silent */ }
        } else {
          this.workspaceName = '';
        }

        if (dossierData.is_analysing === 'pending') {
          this.startPolling();
          this.analysesFiscales = [];
        } else if (dossierData.is_analysing === 'done') {
          this.stopPolling();
          await this.fetchAnalysisPoints(dossierId);
          this.maybeFetchRedactionDocument();
        } else {
          this.analysesFiscales = [];
        }

        // Default expand timeline only if streaming
        this.$set(this.expandedTimelineMap, 'a-main', dossierData.is_analysing !== 'done');
      } catch (e) {
        console.error(e);
        this.error = this.$t('agent.errors.fetch');
      } finally {
        this.loading = false;
      }
    },

    async fetchAnalysisPoints(dossierId) {
      const { data: pointsData, error } = await supabase
        .from('analysis_points')
        .select('*')
        .eq('dossier_id', dossierId)
        .order('point_index')
        .order('created_at');

      if (error || !pointsData || !pointsData.length) {
        this.analysesFiscales = [];
        return;
      }

      const firstPoint = pointsData[0];
      const isOldFormat = firstPoint.point_data && Array.isArray(firstPoint.point_data.analyses);

      if (isOldFormat) {
        this.analysesFiscales = pointsData.map(dbItem => dbItem.point_data);
      } else {
        this.analysesFiscales = pointsData.map(dbItem => {
          const transformedPoint = { ...dbItem.point_data };
          transformedPoint.analyses = [];
          for (const sourceKey in SOURCE_COLUMN_MAP) {
            const colName = SOURCE_COLUMN_MAP[sourceKey];
            if (dbItem[colName] && Array.isArray(dbItem[colName]) && dbItem[colName].length > 0) {
              transformedPoint.analyses.push({ source: sourceKey, analysis: dbItem[colName] });
            }
          }
          return transformedPoint;
        });
      }
      // Hoist workspace_citations de point_data pour la résolution des chips [doc:N]
      this.workspaceCitationsMap = (this.analysesFiscales[0] && this.analysesFiscales[0].workspace_citations) || {};
    },

    async loadUser() {
      try {
        const user = await getCurrentUser();
        if (user?.email) {
          this.userEmail = user.email;
          this.userInitial = user.email.charAt(0).toUpperCase();
        }
      } catch (e) { /* silent */ }
    },

    // ============ POLLING ============
    startPolling() {
      this.stopPolling();
      this.pollingInterval = setInterval(() => this.pollStatus(), 3000);
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    async pollStatus() {
      try {
        const user = await getCurrentUser();
        if (!user) return;
        const { data, error } = await supabase
          .from('dossiers')
          .select('*')
          .eq('id', this.currentDossierId)
          .eq('user_id', user.id)
          .single();
        if (error) return;

        const prev = this.isAnalysing;
        this.dossierData = data;
        this.dossierName = data.name || this.dossierName;
        this.isAnalysing = data.is_analysing;

        if (prev === 'pending' && data.is_analysing === 'done') {
          this.stopPolling();
          // Le nouveau turn est dans synthese.turns[] désormais ; on dégage les
          // placeholders optimistes pour ne pas dupliquer le rendu.
          this.followUpMessages = [];
          this.isFollowUpPending = false;
          this.pendingFollowUpId = null;
          // Mode rédaction : re-fetch du document (nom + téléchargement)
          // après chaque run terminé, initial comme follow-up.
          this.maybeFetchRedactionDocument();
          setTimeout(() => this.fetchAnalysisPoints(this.currentDossierId), 1200);
          this.$nextTick(() => this.scrollToBottom());
        } else if (data.is_analysing === 'failed') {
          this.stopPolling();
          this.isFollowUpPending = false;
          this.pendingFollowUpId = null;
        } else if (data.is_analysing === 'done') {
          this.stopPolling();
        }
      } catch (e) {
        console.error(e);
      }
    },

    // ============ MODE RÉDACTION (document rédigé) ============
    maybeFetchRedactionDocument() {
      // Fetch inconditionnel (404 toléré par fetchRedactionDocument) : même
      // quand le tour n'a produit aucun document (clause de doute), la ligne
      // mémoire du dossier porte template_id, sans lequel les follow-ups
      // repartiraient silencieusement en agent classique hors template.
      this.fetchRedactionDocument();
    },

    async fetchRedactionDocument() {
      if (!this.currentDossierId || this.isFetchingRedactionDoc) return;
      this.isFetchingRedactionDoc = true;
      try {
        const session = await supabase.auth.getSession();
        const token = session?.data?.session?.access_token;
        const resp = await axios.get(
          `${process.env.VUE_APP_API_URL || 'https://api.query-juriste.com'}/redacteur/api/v1/dossiers/${this.currentDossierId}/document`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        this.redactionDoc = resp.data || null;
        // Mémorisé pour que les follow-ups de CE dossier restent une
        // rédaction dans le même template (body de /copilot/agent/follow-up)
        this.redactionTemplateId = (resp.data && resp.data.template_id) || null;
      } catch (error) {
        // 404 = aucun document persisté : le panneau retombe sur les
        // métadonnées de la synthèse (nom du fichier, téléchargement inactif)
        if (error?.response?.status !== 404) {
          console.error('Fetch redaction document failed:', error);
        }
      } finally {
        this.isFetchingRedactionDoc = false;
      }
    },

    // Téléchargement client du docx (b64 -> Blob), même logique que
    // RedacteurPage.downloadFile.
    downloadRedactionDocument() {
      const file = this.redactionDoc && this.redactionDoc.file;
      if (!file || !file.data) return;
      try {
        const byteChars = atob(file.data);
        const bytes = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
        const blob = new Blob([bytes], {
          type: file.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name || this.redactionFileName;
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error('Download redaction document failed:', e);
        this.$toast?.error('Le téléchargement du document a échoué.');
      }
    },

    // ============ TIMELINE BUILD ============
    // En mode AGENT, la vraie timeline du raisonnement est émise par le backend
    // dans le champ `synthese` (JSON) — pending : { status, current_iteration, steps }
    // done : { timeline, iterations, answer }. On l'utilise tel quel.
    parseAgentTimeline() {
      const raw = this.dossierData?.synthese;
      if (!raw) return null;
      let parsed;
      try {
        parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      } catch (e) { return null; }
      if (!parsed) return null;
      // pending → {status, current_iteration, steps}
      if (Array.isArray(parsed.steps) && parsed.steps.length) {
        return { steps: parsed.steps, iterations: parsed.current_iteration || 1 };
      }
      // done → {timeline, iterations, answer}
      if (Array.isArray(parsed.timeline) && parsed.timeline.length) {
        return { steps: parsed.timeline, iterations: parsed.iterations || 1 };
      }
      return null;
    },

    mapBackendStep(raw) {
      // Convertit un step du backend Agent vers notre format d'affichage.
      if (!raw || typeof raw !== 'object') return null;
      const iter = raw.iteration;
      if (raw.type === 'reasoning') {
        return { type: 'reasoning', content: raw.content || '', iter };
      }
      if (raw.type === 'tool_call' && raw.tool === 'query_juriste') {
        // pending steps → `instruction` at top level
        // done timeline → `arguments.instruction`
        const instruction = raw.instruction || raw.arguments?.instruction || raw.arguments?.query || '';
        return { type: 'search', instruction, iter };
      }
      if (raw.type === 'tool_call' && raw.tool === 'get_exact_doc') {
        const ref = raw.reference || raw.arguments?.reference || raw.source_id || this.$t('agent.docNames.default');
        return { type: 'doc', ref, iter };
      }
      if (raw.type === 'tool_result' && raw.tool === 'query_juriste') {
        return {
          type: 'result',
          nb: raw.nb_docs || 0,
          sourcesUsed: Array.isArray(raw.sources_used) ? raw.sources_used : [],
          iter,
        };
      }
      if (raw.type === 'tool_result' && raw.tool === 'get_exact_doc') {
        return {
          type: 'doc_result',
          title: raw.title || raw.source_id || this.$t('agent.docNames.default'),
          error: raw.error || null,
          iter,
        };
      }
      if (raw.type === 'tool_call' && raw.tool === 'query_workspace') {
        const question = raw.question || raw.arguments?.question || '';
        return { type: 'workspace', question, iter };
      }
      if (raw.type === 'tool_result' && raw.tool === 'query_workspace') {
        return {
          type: 'workspace_result',
          answerPreview: raw.answer_preview || '',
          costUsd: raw.cost_usd || 0,
          error: raw.error || null,
          iter,
        };
      }
      if (raw.type === 'tool_call' && raw.tool === 'query_web') {
        const instruction = raw.instruction || raw.arguments?.instruction || '';
        return { type: 'web', instruction, iter };
      }
      if (raw.type === 'tool_result' && raw.tool === 'query_web') {
        return {
          type: 'web_result',
          answerPreview: raw.answer_preview || '',
          error: raw.error || null,
          iter,
        };
      }
      if (raw.type === 'tool_call' && raw.tool === 'query_parliament') {
        const instruction = raw.instruction || raw.arguments?.instructions || '';
        return { type: 'parliament', instruction, iter };
      }
      if (raw.type === 'tool_result' && raw.tool === 'query_parliament') {
        return {
          type: 'parliament_result',
          answerPreview: raw.answer_preview || '',
          costUsd: raw.cost_usd || 0,
          error: raw.error || null,
          iter,
        };
      }
      if (raw.type === 'answering') {
        return { type: 'answering', iter };
      }
      return null;
    },

    buildTimeline() {
      const real = this.parseAgentTimeline();
      if (real && real.steps.length) {
        const mapped = real.steps.map(s => this.mapBackendStep(s)).filter(Boolean);
        if (mapped.length) return mapped;
      }
      // Fallback (ex : vieux dossiers sans timeline enregistrée)
      if (this.isAnalysing !== 'done') {
        return [{ type: 'reasoning', content: this.$t('agent.fallback.analyzing') }];
      }
      return [{ type: 'answering' }];
    },

    // ============ SYNTHESE RENDERING ============
    unwrapSynthese(raw) {
      // En mode AGENT la synthese est stockée comme JSON {"answer": "<markdown>"}.
      if (typeof raw !== 'string') return raw || '';
      const trimmed = raw.trim();
      if (trimmed.startsWith('{')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed && typeof parsed.answer === 'string') return parsed.answer;
        } catch (e) { /* fall through */ }
      }
      return raw;
    },

    getSourcesMapForPoint(point) {
      // Construit une map { source_key: [docs] } à partir d'un analysis_point
      // (turn-spécifique). Retourne {} si le point n'existe pas.
      const map = {};
      if (!point || !Array.isArray(point.analyses)) return map;
      for (const block of point.analyses) {
        if (block.source && Array.isArray(block.analysis)) {
          map[block.source] = block.analysis;
        }
      }
      return map;
    },

    renderSyntheseHtml(synthese, turnIdx = 0) {
      if (!synthese) return '';
      const content = this.unwrapSynthese(synthese);
      if (!content) return '';

      let html;
      if (/^\s*<\w+/.test(content)) {
        // Synthèse déjà stockée en HTML (legacy) → on la garde telle quelle.
        html = content;
      } else {
        // Markdown → HTML (titres, gras, italique, listes, tableaux GFM, …)
        html = this.formatText(content);
      }

      // 1) Tokens classiques « Legiarti - 1 », « Bofip - 2 », etc.
      html = this.processSynthesisReferencesInText(html, turnIdx);
      // 2) Citations en langage naturel « (CE, 28 oct 2025, 502486) », « (Doctrine #2) », « (Article 223 B - CGI) »
      html = this.linkNaturalCitations(html, turnIdx);
      // 3) Citations workspace [doc:N] → chips cliquables
      html = this.injectWorkspaceCitations(html);
      return html;
    },

    // Numéro stable par passage cité (ordre de première apparition) — le même
    // node_id garde son numéro sur tous les rendus et les tours suivants.
    wsCitationNumber(id) {
      if (!this._wsCiteNums) this._wsCiteNums = {};
      if (!(id in this._wsCiteNums)) {
        this._wsCiteNums[id] = Object.keys(this._wsCiteNums).length + 1;
      }
      return this._wsCiteNums[id];
    },

    injectWorkspaceCitations(html) {
      if (!html) return html;
      const map = this.workspaceCitationsMap || {};
      const esc = (s) => this.escapeHtml(String(s));
      return html.replace(/\[doc:(\d+)\]/g, (m, id) => {
        const meta = map[String(id)] || {};
        const full = meta.source_file || '';
        // Marqueur sans fichier derrière (racine/dossier cité, ID inconnu) :
        // rien à prévisualiser → on retire proprement le marqueur du texte.
        if (!full) return '';
        const fname = full.split('/').pop();
        let pages = '';
        if (meta.page_start != null) {
          pages = (meta.page_end != null && meta.page_end !== meta.page_start)
            ? ` p.${meta.page_start}-${meta.page_end}`
            : ` p.${meta.page_start}`;
        }
        const n = this.wsCitationNumber(String(id));
        return `<a href="#" class="workspace-citation" data-workspace-node-id="${id}" data-ws-tip="${esc(fname + pages)}">[${n}]</a>`;
      });
    },

    // Rendu d'un bloc tableau GFM isolé via marked, wrappé pour le scroll
    // horizontal. Seuls les tableaux passent par marked ; toute la prose
    // continue d'utiliser le pipeline regex historique (cf. renderProse).
    renderTableBlock(block) {
      const html = marked.parse(block, { gfm: true, breaks: false });
      return html
        .replace(/<table(\s[^>]*)?>/g, '<div class="synthese-table-wrap"><table$1>')
        .replace(/<\/table>/g, '</table></div>');
    },

    // Pipeline markdown → HTML historique (porté de DossierPage.vue). Appliqué
    // uniquement aux segments de prose (hors tableaux). Ne PAS y toucher : le
    // rendu de la prose doit rester identique à l'existant.
    renderProse(text) {
      let t = String(text);
      // 1) On extrait d'abord les liens markdown [titre](url) et on les
      // remplace par un placeholder neutre. Sans ça, la regex italique
      // `_..._` ci-dessous mange les underscores des URLs (cas observé :
      // une URL finit par `_en` → rendu cassé avec `</u>en` qui traîne).
      const links = [];
      t = t.replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, (_m, label, url) => {
        const safeUrl = url.replace(/"/g, '&quot;');
        const idx = links.length;
        links.push(`<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="web-link">${label}</a>`);
        return `@@MDLINK${idx}@@`;
      });
      t = t.replace(/\n\s*\*\*\*\s*\n/g, '\n\n');
      t = t.replace(/####\s+(.*?)(?:\n|$)/g, '<h3><strong>$1</strong></h3>');
      t = t.replace(/###\s+(.*?)(?:\n|$)/g, '<h3><strong>$1</strong></h3>');
      t = t.replace(/##\s+(.*?)(?:\n|$)/g, '<h3><strong>$1</strong></h3>');
      t = t.replace(/#\s+(.*?)(?:\n|$)/g, '<h3><strong>$1</strong></h3>');
      t = t.replace(/(^|\n)( *)\*\s+(.*?)(?=\n|$)/g, (m, sep, indent, content) => {
        const level = Math.floor(indent.length / 4) + 1;
        const bullet = level === 1 ? '-' : level === 2 ? '•' : level === 3 ? '◦' : '▪';
        return `${sep}<li style="margin-left:${level}em; margin-top:0.4em; margin-bottom:0.4em;">${bullet} ${content}</li>`;
      });
      t = t.replace(/(<li[\s\S]*?<\/li>)+/g, m => `<ul>${m}</ul>`);
      t = t.replace(/<\/ul>\s*<ul>/g, '');
      t = t.replace(/\n/g, '<br>');
      t = t.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      t = t.replace(/_(.*?)_/g, '<u>$1</u>');
      // 2) Réinjection des liens markdown à leur place
      t = t.replace(/@@MDLINK(\d+)@@/g, (_m, idx) => links[Number(idx)] || '');
      return t;
    },

    // Markdown → HTML. Approche hybride : la prose garde le rendu historique
    // (renderProse), seuls les blocs tableau GFM passent par marked
    // (renderTableBlock). On découpe le texte en segments {prose|table} en
    // détectant les blocs tableau (ligne d'en-tête + ligne de séparation
    // `|---|`), puis on assemble et on assainit avec DOMPurify.
    formatText(text) {
      if (!text) return '';
      const lines = String(text).split('\n');
      const segments = []; // { type: 'prose'|'table', lines: [] }
      const isTableRow = (l) => /\|/.test(l) && l.trim() !== '';
      const isDelimiter = (l) => /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/.test(l) && /\|/.test(l);

      let i = 0;
      let proseBuf = [];
      const flushProse = () => {
        if (proseBuf.length) { segments.push({ type: 'prose', lines: proseBuf }); proseBuf = []; }
      };
      while (i < lines.length) {
        // Début de tableau : ligne d'en-tête avec « | » suivie d'une ligne de séparation.
        if (isTableRow(lines[i]) && i + 1 < lines.length && isDelimiter(lines[i + 1])) {
          flushProse();
          const tbl = [lines[i], lines[i + 1]];
          i += 2;
          while (i < lines.length && isTableRow(lines[i])) { tbl.push(lines[i]); i += 1; }
          segments.push({ type: 'table', lines: tbl });
        } else {
          proseBuf.push(lines[i]);
          i += 1;
        }
      }
      flushProse();

      const html = segments.map((seg) => {
        if (seg.type === 'table') return this.renderTableBlock(seg.lines.join('\n'));
        return this.renderProse(seg.lines.join('\n'));
      }).join('');

      return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'strong', 'em', 'u', 'br', 'li', 'ul', 'ol', 'p', 'div', 'span', 'a', 'table', 'thead', 'tbody', 'tr', 'th', 'td'],
        ALLOWED_ATTR: ['style', 'class', 'href', 'target', 'rel', 'align'],
      });
    },

    // Résout les citations parenthésées en langage naturel vers des hyperliens
    linkNaturalCitations(html) {
      // turnIdx ignoré : Fix #3 fait une résolution cross-turns globale.
      if (!this.analysesFiscales || !this.analysesFiscales.length) return html;

      // Fix #3 — agréger les blocks de TOUS les turns, en gardant le pointIndex
      // d'origine (pour que les liens posés pointent toujours sur le bon
      // analysis_point row). Sans ça, une citation en T2 vers un doc trouvé en
      // T1 (ex: « Article 1240 ») n'était jamais résolue.
      const allBlocks = []; // {source, analysis, pointIndex}
      for (let i = 0; i < this.analysesFiscales.length; i += 1) {
        const point = this.analysesFiscales[i];
        if (!point || !Array.isArray(point.analyses)) continue;
        for (const block of point.analyses) {
          if (block.source && Array.isArray(block.analysis)) {
            allBlocks.push({ source: block.source, analysis: block.analysis, pointIndex: i });
          }
        }
      }
      if (!allBlocks.length) return html;

      const esc = (s) => this.escapeHtml(s);
      const makeLink = (doc, source, idx, pointIdx) => {
        const title = this.getDocumentTitleForSynthesis(doc, source, idx);
        const link = doc.official_link
          ? ` <a href="${esc(doc.official_link)}" target="_blank" rel="noopener noreferrer" class="official-link-icon" title="${esc(this.$t('agent.officialLinkTitle'))}">↗</a>`
          : '';
        return `<a href="#" class="synthesis-reference" data-source="${source}" data-index="${idx}" data-point-index="${pointIdx}">${esc(title)}</a>${link}`;
      };

      // 1) (Doctrine #N) — N est un index 1-based sur l'ensemble des bofip cumulés
      const bofipBlocks = allBlocks.filter(b => b.source === 'bofip');
      if (bofipBlocks.length) {
        html = html.replace(/\(Doctrine\s*#\s*(\d+)\)/gi, (m, n) => {
          let need = parseInt(n, 10);
          for (const b of bofipBlocks) {
            if (need <= b.analysis.length) {
              const i = need - 1;
              return `(${makeLink(b.analysis[i], 'bofip', i, b.pointIndex)})`;
            }
            need -= b.analysis.length;
          }
          return m;
        });
      }

      // 2) (Article X - <Code>) — recherche fuzzy sur tous les turns
      const legiartiBlocks = allBlocks.filter(b => b.source === 'legiarti');
      if (legiartiBlocks.length) {
        html = html.replace(/\(Article\s+([^()-]+?)\s*-\s*([^()]+)\)/gi, (match, art) => {
          if (match.includes('synthesis-reference')) return match;
          const artNorm = String(art).trim().replace(/\s+/g, '').toLowerCase();
          for (const block of legiartiBlocks) {
            for (let i = 0; i < block.analysis.length; i += 1) {
              const d = block.analysis[i];
              const fields = [d.article_number, d.article, d.title, d.titre, d.textNumber]
                .filter(Boolean).map(v => String(v).replace(/\s+/g, '').toLowerCase());
              if (fields.some(f => f.includes(artNorm) || artNorm.includes(f))) {
                return `(${makeLink(d, 'legiarti', i, block.pointIndex)})`;
              }
            }
          }
          return match;
        });
      }

      // 3) Matching générique par chevauchement de nombres ≥4 chiffres
      const candidates = [];
      for (const block of allBlocks) {
        for (let i = 0; i < block.analysis.length; i += 1) {
          const d = block.analysis[i];
          const sig = [
            d.textNumber, d.numero, d.num_affaire, d.numero_affaire,
            d.reference, d.identifiant, d.title, d.titre,
            d.article_number, d.date, d.date_decision, d.date_acte,
            d.siren,
          ].filter(Boolean).map(String).join(' ');
          const nums = sig.match(/\d{4,}/g) || [];
          if (nums.length) candidates.push({ source: block.source, idx: i, doc: d, nums, pointIndex: block.pointIndex });
        }
      }

      if (candidates.length) {
        html = html.replace(/\(([^()]+)\)/g, (match, inner) => {
          if (inner.includes('synthesis-reference')) return match;
          const pnums = inner.match(/\d{4,}/g);
          if (!pnums || !pnums.length) return match;
          for (const c of candidates) {
            if (c.nums.some(n => pnums.includes(n))) {
              return `(${makeLink(c.doc, c.source, c.idx, c.pointIndex)})`;
            }
          }
          return match;
        });
      }

      return html;
    },

    // Porté depuis DossierPage.vue (branche FLASH/AGENT) — AgentPage a 1 analysis_point
    // par turn (turn 0, 1, 2...). Chaque turn résout ses citations contre ses propres docs.
    processSynthesisReferencesInText(text, turnIdx = 0) {
      if (!text || !this.analysesFiscales || !this.analysesFiscales.length) return text;

      const expandedText = this.expandMultiReferences(text);
      const refRegex = new RegExp(`(\\[?(?:ID:\\s+)?(?:${REF_TYPES})\\s*-\\s*\\d+(?:\\s*-\\s*\\d+)?\\]?)`, 'g');

      // Fix #3 — résolution cumulée cross-turns.
      // Le backend numérote désormais les docs en continu sur tous les tours
      // (ex: T1 = Legiarti-1..3, T2 = Legiarti-4..). On cherche donc l'ID
      // demandé en scannant les analysesFiscales dans l'ordre, en cumulant
      // un offset par source. Le `turnIdx` n'est plus que l'index « par défaut »
      // si l'ID demandé tombe avant le début des docs cumulés (compat
      // ascendante avec les anciens dossiers numérotés par turn).
      const resolveRef = (sourceKey, refIndex1Based) => {
        let cumulative = 0;
        for (let i = 0; i < this.analysesFiscales.length; i += 1) {
          const point = this.analysesFiscales[i];
          if (!point) continue;
          const block = (point.analyses || []).find(b => b.source === sourceKey);
          const docs = (block && block.analysis) || [];
          if (refIndex1Based <= cumulative + docs.length) {
            const localIdx = refIndex1Based - cumulative - 1;
            return { doc: docs[localIdx], pointIndex: i, localIdx };
          }
          cumulative += docs.length;
        }
        // Fallback compat ascendante : ancien dossier où chaque turn redémarre
        // à 1 et où la numérotation cross-turns ne s'applique pas. On retombe
        // sur l'ancien comportement (résolution dans la row du turn courant).
        const point = this.analysesFiscales[turnIdx];
        if (point) {
          const block = (point.analyses || []).find(b => b.source === sourceKey);
          const docs = (block && block.analysis) || [];
          const localIdx = refIndex1Based - 1;
          if (docs[localIdx]) {
            return { doc: docs[localIdx], pointIndex: turnIdx, localIdx };
          }
        }
        return null;
      };

      return expandedText.replace(refRegex, (match) => {
        const refMatch = match.match(new RegExp(`^\\[?(?:ID:\\s+)?(${REF_TYPES})\\s*-\\s*(\\d+)(?:\\s*-\\s*\\d+)?\\]?$`));
        if (!refMatch) return match;

        const refType = refMatch[1];
        const refIndex1Based = parseInt(refMatch[2], 10);

        const sourceKey = this.mapRefTypeToSourceKey(refType);
        if (!sourceKey) return `<span class="inactive-reference">${match}</span>`;

        const found = resolveRef(sourceKey, refIndex1Based);
        if (found && found.doc) {
          const documentTitle = this.getDocumentTitleForSynthesis(found.doc, sourceKey, found.localIdx);
          const officialLink = found.doc.official_link;
          let linkIconHtml = '';
          if (officialLink) {
            const safeLink = this.escapeHtml(officialLink);
            linkIconHtml = ` <a href="${safeLink}" target="_blank" rel="noopener noreferrer" class="official-link-icon" title="${this.escapeHtml(this.$t('agent.officialLinkTitle'))}">↗</a>`;
          }
          return `<a href="#" class="synthesis-reference" data-source="${sourceKey}" data-index="${found.localIdx}" data-point-index="${found.pointIndex}">${this.escapeHtml(documentTitle)}</a>${linkIconHtml}`;
        }

        return `<span class="inactive-reference">${match}</span>`;
      });
    },

    // Porté depuis DossierPage.vue
    expandMultiReferences(text) {
      if (!text) return '';
      const newMultiRefRegex = new RegExp(`(\\[?(?:ID:\\s+)?(?:${REF_TYPES})\\]?)(\\s*(?:-\\s*|\\s+))(\\d+\\s*-\\s*\\d+(?:\\s*,\\s*\\d+\\s*-\\s*\\d+)*)`, 'g');
      const oldMultiRefRegex = new RegExp(`(\\[?(?:ID:\\s+)?(?:${REF_TYPES})\\]?)(\\s*(?:-\\s*|\\s+))(\\d+(?:\\s*,\\s*\\d+)*)`, 'g');

      let result = text.replace(newMultiRefRegex, (match, refType, sep, numbersPart) => {
        const references = numbersPart.split(/\s*,\s*/);
        return references.map(ref => `${refType} - ${ref}`).join(', ');
      });

      result = result.replace(oldMultiRefRegex, (match, refType, sep, numbersPart) => {
        if (match.includes(' - ') && /\d+\s*-\s*\d+/.test(match)) return match;
        const numbers = numbersPart.split(/\s*,\s*/);
        return numbers.map(num => `${refType} - ${num}`).join(', ');
      });

      return result;
    },

    // Porté depuis DossierPage.vue
    mapRefTypeToSourceKey(refType) {
      if (refType === 'Jurisprudence') return 'juritext';
      if (refType === 'Legiarti') return 'legiarti';
      if (refType === 'Bofip' || refType === 'BOFiP' || refType === 'BOFIP') return 'bofip';
      if (refType === 'Euritext') return 'euritext';
      if (refType === 'CNN') return 'cnn';
      if (refType === 'BALO') return 'balo';
      if (refType === 'Statuts') return 'statuts';
      if (refType === 'Convbi') return 'convbi';
      if (refType === 'Infofi' || refType === 'Infofin') return 'infofi';
      if (refType === 'ActesSocietes') return 'actes_societes';
      if (['Reglements', 'Reglement', 'Règlements', 'Règlement'].includes(refType)) return 'reglements';
      if (refType === 'BODACC') return 'bodacc';
      if (refType === 'Energy') return 'energy';
      if (refType === 'Concurrence') return 'concurrence';
      if (refType === 'INPI') return 'inpi';
      if (refType === 'Mention') return 'mention';
      if (refType === 'CNIL') return 'cnil';
      if (refType === 'BOSS') return 'boss';
      if (refType === 'ACPR') return 'acpr';
      if (refType === 'AMF') return 'amf';
      if (refType === 'CompCase') return 'eccomp';
      if (refType === 'UPC') return 'upc';
      if (refType === 'EDPB') return 'edpb';
      if (refType === 'AGCM') return 'agcm';
      if (refType === 'GiustiziaAmm') return 'giustizia_amministrativa';
      if (refType === 'EuritextITA') return 'jurisprudence_europe_italy';
      if (refType === 'EurLexITA') return 'eurlex_italy';
      return null;
    },

    // Porté depuis DossierPage.vue
    getDocumentFromPoint(point, sourceKey, docIndex) {
      if (!point) return null;
      if (sourceKey === 'mention') {
        if (point.mentioned_docs && point.mentioned_docs[docIndex]) return point.mentioned_docs[docIndex];
        return null;
      }
      if (!point.analyses) return null;
      const sourceBlock = point.analyses.find(block => block.source === sourceKey);
      if (sourceBlock && sourceBlock.analysis && sourceBlock.analysis[docIndex]) {
        return sourceBlock.analysis[docIndex];
      }
      return null;
    },

    // Porté depuis DossierPage.vue — construit le titre affiché pour chaque type de source
    getDocumentTitleForSynthesis(document, sourceKey, index) {
      if (!document) return this.$t('agent.docs.default', { n: index + 1 });

      if (sourceKey === 'bofip' && document.identifiant) return document.identifiant;
      if (sourceKey === 'statuts' && document.pdf_name) return this.getStatutsTitleForSynthesis(document.pdf_name);
      if (sourceKey === 'reglements') return document.title || this.$t('agent.docs.reglement', { n: index + 1 });

      if (sourceKey === 'infofi') {
        let title = document.title || '';
        if (document.nom_societe && document['Titre opération']) {
          title = `${document.nom_societe} - ${document['Titre opération']}`;
        } else if (document.nom_societe) {
          title = document.nom_societe;
        } else if (document['Titre opération']) {
          title = document['Titre opération'];
        }
        return title || this.$t('agent.docs.info', { n: index + 1 });
      }

      if (sourceKey === 'actes_societes') {
        if (document.siren && document.date_acte) {
          return this.$t('agent.docs.acteSociete', { siren: document.siren, date: this.formatActesSocietesDate(document.date_acte) });
        }
        if (document.title) return document.title;
        return this.$t('agent.docs.acteSocieteDefault', { n: index + 1 });
      }

      if (sourceKey === 'concurrence') {
        if (document.numero_affaire && document.date_decision) {
          return this.$t('agent.docs.autoriteConcurrence', { date: document.date_decision, num: document.numero_affaire });
        }
        if (document.titre) return document.titre;
        return this.$t('agent.docs.decisionConcurrence', { n: index + 1 });
      }

      if (sourceKey === 'inpi') {
        if (document.reference && document.date) return this.$t('agent.docs.inpiRef', { date: document.date, ref: document.reference });
        if (document.parties) return document.parties;
        return this.$t('agent.docs.decisionInpi', { n: index + 1 });
      }

      if (sourceKey === 'cnil') return document.title || document.titre_complet || this.$t('agent.docs.decisionCnil', { n: index + 1 });
      if (sourceKey === 'boss') return document.title || this.$t('agent.docs.sectionBoss', { n: index + 1 });
      if (sourceKey === 'acpr') return document.title || document.numero || this.$t('agent.docs.docAcpr', { n: index + 1 });
      if (sourceKey === 'amf') return document.title || document.reference || this.$t('agent.docs.docAmf', { n: index + 1 });

      if (sourceKey === 'mention') {
        if (document.title) return document.title;
        const parts = [];
        if (document.type) parts.push(document.type.charAt(0).toUpperCase() + document.type.slice(1));
        if (document.jurisdiction) {
          if (!document.type || !document.type.toLowerCase().includes(document.jurisdiction.toLowerCase())) {
            parts.push(document.jurisdiction);
          }
        }
        if (document.date) {
          try {
            const dateObj = new Date(document.date);
            if (!isNaN(dateObj.getTime())) parts.push(this.$localeDate(dateObj));
            else parts.push(document.date);
          } catch (e) { parts.push(document.date); }
        }
        if (parts.length) return parts.join(' - ');
        return this.$t('agent.docs.docCited', { n: index + 1 });
      }

      return document.title || document.textNumber || this.getDefaultDocumentName(sourceKey, index);
    },

    formatActesSocietesDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return this.$localeDate(date, { day: '2-digit', month: '2-digit', year: 'numeric' });
        }
      } catch (e) { return dateString; }
      return dateString;
    },

    getStatutsTitleForSynthesis(pdfName) {
      if (!pdfName) return '';
      return pdfName.replace(/\.pdf$/i, '');
    },

    getDefaultDocumentName(sourceKey, index) {
      const nameKeys = {
        legiarti: 'agent.docNames.legiarti', juritext: 'agent.docNames.juritext',
        bofip: 'agent.docNames.bofip', euritext: 'agent.docNames.euritext',
        cnn: 'agent.docNames.cnn', balo: 'agent.docNames.balo',
        bodacc: 'agent.docNames.bodacc', statuts: 'agent.docNames.statuts',
        convbi: 'agent.docNames.convbi', infofi: 'agent.docNames.infofi',
        actes_societes: 'agent.docNames.actes_societes',
        reglements: 'agent.docNames.reglements', concurrence: 'agent.docNames.concurrence',
        inpi: 'agent.docNames.inpi', cnil: 'agent.docNames.cnil',
        boss: 'agent.docNames.boss', acpr: 'agent.docNames.acpr',
        amf: 'agent.docNames.amf',
      };
      const key = nameKeys[sourceKey];
      const name = key ? this.$t(key) : this.$t('agent.docNames.default');
      return `${name} #${index + 1}`;
    },

    onSyntheseClick(ev) {
      // Les icônes lien officiel ont leur propre comportement (nouvel onglet) — ne pas intercepter
      if (ev.target.closest('.official-link-icon')) return;

      // 1) Citation workspace [doc:N] → preview PDF
      const wsChip = ev.target.closest('.workspace-citation');
      if (wsChip) {
        ev.preventDefault();
        const nodeId = wsChip.getAttribute('data-workspace-node-id');
        if (nodeId) this.openWorkspaceCitation(nodeId);
        return;
      }

      // 2) Références juridiques standards
      const el = ev.target.closest('.synthesis-reference');
      if (!el) return;
      ev.preventDefault();
      const source = el.getAttribute('data-source');
      const n = parseInt(el.getAttribute('data-index'), 10);
      const turnIdx = parseInt(el.getAttribute('data-point-index'), 10) || 0;
      if (source && !isNaN(n)) this.openDoc(source, n, turnIdx);
    },

    openWorkspaceCitation(nodeId) {
      const meta = this.workspaceCitationsMap[String(nodeId)];
      if (!meta) {
        console.warn('Citation workspace introuvable pour', nodeId, '— IDs dispos:', Object.keys(this.workspaceCitationsMap));
        return;
      }
      this.citationModalData = {
        nodeId: String(nodeId),
        workspaceId: this.dossierData && this.dossierData.workspace_id,
        ...meta,
      };
      this.showCitationModal = true;
    },

    // ============ SOURCES / DOCS ============
    openDoc(source, n, turnIdx = 0) {
      const point = this.analysesFiscales[turnIdx];
      if (!point) return;
      const doc = this.getDocumentFromPoint(point, source, n);
      if (!doc) return;

      this.articleDetailData = buildArticleDetailData(doc, source, n);

      // MENTION fallback mapping (same as DossierPage)
      if (source === 'mention') {
        this.articleDetailData.url = doc.link || doc.official_link;
        this.articleDetailData.content = doc.content || doc.takeaway || doc.full_text || '';
        this.articleDetailData.type = doc.type;
        this.articleDetailData.jurisdiction = doc.jurisdiction;
        this.articleDetailData.date = doc.date;
        this.articleDetailData.original_reference = doc.original_reference;
        if (!this.articleDetailData.title) {
          this.articleDetailData.title = this.getDocumentTitleForSynthesis(doc, source, n);
        }
      }

      this.docPane = { source, n };
      this.showArticleDetailPanel = true;
      document.body.style.overflow = 'hidden';
    },

    closeDoc() {
      this.closeArticleDetailPanel();
    },

    closeArticleDetailPanel() {
      this.showArticleDetailPanel = false;
      this.docPane = null;
      this.articleDetailData = {
        title: '',
        content: '',
        source: '',
        sourceData: null,
      };
      document.body.style.overflow = '';
    },

    setActiveSource(msgId, sourceKey) {
      this.$set(this.activeSourceMap, msgId, sourceKey);
    },

    sourceShort(key) {
      return SOURCE_LABELS[key]?.short || key;
    },

    sourceDisplayName(key) {
      return SOURCE_LABELS[key]?.long || key;
    },

    docTitle(doc, sourceKey) {
      if (!doc) return '';
      if (doc.title) return doc.title;
      if (doc.titre) return doc.titre;
      if (doc.textNumber) return doc.textNumber;
      if (doc.reference) return doc.reference;
      if (sourceKey === 'juritext' && doc.juris_type) return `${doc.juris_type}, ${doc.date || ''}`;
      return doc.identifiant || this.$t('agent.docNames.default');
    },

    docSub(doc) {
      if (!doc) return '';
      if (doc.subtitle) return doc.subtitle;
      if (doc.sub) return doc.sub;
      if (doc.juridiction) return doc.juridiction;
      if (doc.date) return doc.date;
      return '';
    },

    // ============ TOGGLES ============
    toggleTimeline(msgId) {
      const current = this.expandedTimelineMap[msgId] !== false;
      this.$set(this.expandedTimelineMap, msgId, !current);
    },

    isTimelineExpanded(msgId) {
      // Ouverte par défaut (undefined → true), fermée seulement si explicitement set à false.
      return this.expandedTimelineMap[msgId] !== false;
    },

    toggleSources(msgId) {
      this.$set(this.openSourcesMap, msgId, !this.openSourcesMap[msgId]);
    },

    isSourcesOpen(msgId) {
      return !!this.openSourcesMap[msgId];
    },

    renderReasoning(content) {
      if (!content) return '';
      const html = marked.parse(String(content), { gfm: true, breaks: true });
      return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['p', 'strong', 'em', 'ul', 'ol', 'li', 'br', 'code'],
        ALLOWED_ATTR: [],
      });
    },

    tlBubbleClass(type) {
      return {
        reasoning: 'tl-reasoning',
        search: 'tl-search',
        result: 'tl-success',
        doc: 'tl-doc',
        doc_result: 'tl-success',
        workspace: 'tl-doc',
        workspace_result: 'tl-success',
        web: 'tl-search',
        web_result: 'tl-success',
        answering: 'tl-writing',
      }[type] || 'tl-reasoning';
    },

    // ============ COMPOSER / FAKE FOLLOW-UP ============
    autoResizeComposer() {
      const ta = this.$refs.composerTa;
      if (!ta) return;
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, 180) + 'px';
    },

    async sendFollowUp() {
      const q = (this.composerValue || '').trim();
      if (!q || this.isFollowUpPending) return;
      if (!this.dossierData || !this.dossierData.id) return;
      if (!this.canRequest) {
        this.showPaywall = true;
        return;
      }

      const now = new Date();
      const at = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      const pendingId = 'pending-' + Date.now();

      // Push optimiste : la question + un placeholder "réflexion en cours"
      this.followUpMessages.push({ id: 'u-' + pendingId, role: 'user', text: q, at });
      this.followUpMessages.push({
        id: pendingId,
        role: 'agent',
        status: 'streaming',
        at,
        timeline: [{ type: 'reasoning', content: this.$t('agent.fallback.analyzingFollowUp') }],
        syntheseHtml: null,
        sources: {},
        sourceKeys: [],
        totalDocs: 0,
        elapsed: '0s',
        pending: true,
      });
      this.composerValue = '';
      this.isFollowUpPending = true;
      this.pendingFollowUpId = pendingId;
      this.$set(this.expandedTimelineMap, pendingId, true);
      this.$nextTick(() => {
        const ta = this.$refs.composerTa;
        if (ta) ta.style.height = '';
        this.scrollToBottom();
      });

      try {
        const session = await supabase.auth.getSession();
        const token = session?.data?.session?.access_token;
        await axios.post(
          `${process.env.VUE_APP_API_URL || 'https://api.query-juriste.com'}/copilot/agent/follow-up`,
          {
            dossierId: this.dossierData.id,
            question: q,
            // Dossier en mode rédaction : le follow-up reste une rédaction
            // dans le même template (template_id vient du GET document)
            ...(this.redactionTemplateId ? { template_id: this.redactionTemplateId } : {}),
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        // Le backend a mis is_analysing='pending' → on lance le polling
        this.isAnalysing = 'pending';
        this.startPolling();
      } catch (error) {
        console.error('Agent follow-up POST failed:', error);
        const msg = error?.response?.data?.error || this.$t('agent.fallback.analyzingFollowUp');
        // Marque le message pending en erreur
        const idx = this.followUpMessages.findIndex(m => m.id === pendingId);
        if (idx !== -1) {
          this.$set(this.followUpMessages, idx, {
            id: pendingId,
            role: 'agent',
            status: 'failed',
            at,
            syntheseHtml: `<p style="color:#b91c1c">${this.escapeHtml(msg)}</p>`,
            timeline: [],
            sources: {},
            sourceKeys: [],
            totalDocs: 0,
            pending: false,
          });
        }
        this.isFollowUpPending = false;
        this.pendingFollowUpId = null;
      }
    },

    async retryDossier() {
      if (this.isRetrying || !this.dossierData) return;
      this.isRetrying = true;
      try {
        const session = await supabase.auth.getSession();
        const token = session?.data?.session?.access_token;
        await axios.post(
          `${process.env.VUE_APP_API_URL || 'https://api.query-juriste.com'}/copilot/start-query-agent`,
          { dossierId: this.dossierData.id, question: this.dossierData.contexte },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        this.isAnalysing = 'pending';
        this.startPolling();
        this.$set(this.expandedTimelineMap, 'a-main', true);
      } catch (err) {
        console.error('Retry failed:', err);
        const msg = err?.response?.data?.error || 'La relance a échoué.';
        this.$toast?.error(msg);
      } finally {
        this.isRetrying = false;
      }
    },

    findFirstCitableDoc() {
      const analyses = this.analysesFiscales?.[0]?.analyses || [];
      for (const block of analyses) {
        if (block.analysis && block.analysis.length) return block.analysis[0];
      }
      return null;
    },

    // ============ NAVIGATION ============
    goDashboard() {
      this.$router.push('/dashboardPage');
    },

    async exportTurnWord(msg) {
      // Génère un .docx pour le turn donné (question + réponse).
      // msg.turnIdx est l'index du turn dans syntheseTurns.
      if (msg.status !== 'done' || this.exportingTurnIdx != null) return;
      const turnIdx = msg.turnIdx ?? 0;
      const turns = this.syntheseTurns;
      const turn = turns[turnIdx];
      if (!turn) return;

      this.exportingTurnIdx = turnIdx;
      try {
        // Question telle qu'affichée dans la conversation : pour le 1er turn, on
        // privilégie le contexte original du dossier comme dans chatMessages.
        const question = turnIdx === 0
          ? (this.dossierData?.contexte || turn.question || '')
          : (turn.question || '');
        const answerMd = turn.answer || '';

        // Convertir l'HTML rendu (déjà disponible) en paragraphes docx.
        // On préfère parser l'HTML que le markdown brut, parce que renderSyntheseHtml
        // résout déjà les références (Legiarti — N → Article …).
        const html = msg.syntheseHtml || this.renderSyntheseHtml(answerMd, turnIdx) || '';
        const answerParagraphs = this._htmlToDocxParagraphs(html);

        const today = new Date();
        const datePart = today.toISOString().split('T')[0];
        const dossierLabel = (this.dossierName || this.dossierData?.contexte || 'dossier')
          .replace(/[^a-z0-9-_ ]/gi, '')
          .substring(0, 60)
          .trim();
        const turnSuffix = turns.length > 1 ? `-tour${turnIdx + 1}` : '';
        const filename = `${dossierLabel || 'dossier'}${turnSuffix}-${datePart}.docx`;

        const doc = new Document({
          // Sans ce flag, Word étire sur toute la largeur les lignes justifiées
          // terminées par un saut de ligne manuel (<br> -> TextRun break:1)
          compatibility: { doNotExpandShiftReturn: true },
          styles: {
            default: {
              document: { run: { size: 22 } },
              paragraph: { run: { size: 22 } },
            },
          },
          sections: [{
            children: [
              new Paragraph({
                children: [new TextRun({ text: 'Question', bold: true, size: 24 })],
                heading: HeadingLevel.HEADING_2,
                spacing: { after: 120 },
              }),
              new Paragraph({
                children: [new TextRun({ text: question, italics: true })],
                alignment: AlignmentType.JUSTIFIED,
                spacing: { after: 240 },
              }),
              new Paragraph({
                children: [new TextRun({ text: 'Réponse', bold: true, size: 24 })],
                heading: HeadingLevel.HEADING_2,
                spacing: { after: 120 },
              }),
              ...answerParagraphs,
            ],
          }],
        });

        const blob = await Packer.toBlob(doc);
        saveAs(blob, filename);
      } catch (e) {
        console.error('exportTurnWord failed', e);
        if (this.$toast) this.$toast.error('Échec de la génération du document Word');
      } finally {
        this.exportingTurnIdx = null;
      }
    },

    htmlToPlainText(html) {
      // HTML rendu → texte brut lisible (même logique que TransformModal).
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      let out = '';
      const walk = (node) => {
        for (const c of node.childNodes) {
          if (c.nodeType === 3) {
            out += c.textContent;
          } else if (c.tagName === 'BR') {
            out += '\n';
          } else if (c.tagName === 'LI') {
            out += '\n• ';
            for (const x of c.childNodes) walk(x);
          } else if (c.tagName === 'UL' || c.tagName === 'OL') {
            out += '\n';
            for (const x of c.childNodes) walk(x);
          } else if (c.tagName === 'H3' || c.tagName === 'H2' || c.tagName === 'H1') {
            out += '\n\n';
            for (const x of c.childNodes) walk(x);
            out += '\n\n';
          } else if (c.tagName === 'P' || c.tagName === 'DIV' || c.tagName === 'TR') {
            out += '\n';
            for (const x of c.childNodes) walk(x);
          } else if (c.tagName === 'TD' || c.tagName === 'TH') {
            for (const x of c.childNodes) walk(x);
            out += ' | ';
          } else {
            for (const x of c.childNodes) walk(x);
          }
        }
      };
      walk(div);
      return out.replace(/\n{3,}/g, '\n\n').trim();
    },

    _htmlToDocxParagraphs(html) {
      // Mini convertisseur HTML → docx Paragraph[].
      // Gère : <p>, <h1-3>, <ul>/<ol>/<li>, <strong>/<b>, <em>/<i>, <br>, <a>.
      // Ignore les attributs (class, data-*) et chips de citation : on conserve
      // juste le texte des liens.
      //
      // formatText() ne pose pas de <p> autour du texte (juste des <br>), donc
      // au top-level on a souvent une suite de text nodes / <a>réf</a> / <br>.
      // On accumule les nodes inline dans `pending` et on flush en un seul
      // paragraphe quand on rencontre un bloc (heading/list/p) — sinon chaque
      // référence créerait son propre paragraphe (sauts à la ligne parasites).
      if (!html) return [new Paragraph('')];
      const parser = new DOMParser();
      const docHtml = parser.parseFromString(`<div>${html}</div>`, 'text/html');
      const root = docHtml.body.firstChild;
      const out = [];
      let pending = [];

      const flushPending = () => {
        if (!pending.length) return;
        // Trim les <br> en début/fin (pollution visuelle dans Word)
        while (pending.length && pending[0]._isBr) pending.shift();
        while (pending.length && pending[pending.length - 1]._isBr) pending.pop();
        if (pending.length) {
          out.push(new Paragraph({
            children: pending,
            alignment: AlignmentType.JUSTIFIED,
            spacing: { after: 120 },
          }));
        }
        pending = [];
      };

      const mkBreak = () => {
        const r = new TextRun({ text: '', break: 1 });
        r._isBr = true;
        return r;
      };

      const inlineRuns = (node) => {
        const runs = [];
        node.childNodes.forEach((c) => {
          if (c.nodeType === Node.TEXT_NODE) {
            const t = c.textContent;
            if (t) runs.push(new TextRun({ text: t }));
          } else if (c.nodeType === Node.ELEMENT_NODE) {
            const tag = c.tagName.toLowerCase();
            if (tag === 'br') {
              runs.push(new TextRun({ text: '', break: 1 }));
            } else if (tag === 'strong' || tag === 'b') {
              runs.push(new TextRun({ text: c.textContent || '', bold: true }));
            } else if (tag === 'em' || tag === 'i') {
              runs.push(new TextRun({ text: c.textContent || '', italics: true }));
            } else if (tag === 'u') {
              runs.push(new TextRun({ text: c.textContent || '', underline: {} }));
            } else if (tag === 'sup') {
              runs.push(new TextRun({ text: c.textContent || '', superScript: true }));
            } else if (tag === 'a' || tag === 'span') {
              const innerRuns = inlineRuns(c);
              if (innerRuns.length) runs.push(...innerRuns);
              else if (c.textContent) runs.push(new TextRun({ text: c.textContent }));
            } else {
              const t = c.textContent;
              if (t) runs.push(new TextRun({ text: t }));
            }
          }
        });
        return runs;
      };

      const walk = (node) => {
        node.childNodes.forEach((c) => {
          if (c.nodeType === Node.TEXT_NODE) {
            const t = c.textContent;
            if (t) pending.push(new TextRun({ text: t }));
            return;
          }
          if (c.nodeType !== Node.ELEMENT_NODE) return;
          const tag = c.tagName.toLowerCase();

          if (tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'h4') {
            flushPending();
            const lvl = { h1: HeadingLevel.HEADING_1, h2: HeadingLevel.HEADING_2, h3: HeadingLevel.HEADING_3, h4: HeadingLevel.HEADING_4 }[tag];
            out.push(new Paragraph({
              children: [new TextRun({ text: c.textContent || '', bold: true })],
              heading: lvl,
              spacing: { before: 200, after: 120 },
            }));
          } else if (tag === 'div') {
            // Wrap (.synthese-table-wrap) ou conteneur bloc → recurse pour
            // traiter les blocs internes (tableau, listes…). Sinon paragraphe.
            flushPending();
            if (c.querySelector && c.querySelector('table, ul, ol, h1, h2, h3, h4, p')) {
              walk(c);
            } else {
              const runs = inlineRuns(c);
              if (runs.length) {
                out.push(new Paragraph({ children: runs, alignment: AlignmentType.JUSTIFIED, spacing: { after: 120 } }));
              }
            }
          } else if (tag === 'p') {
            flushPending();
            const runs = inlineRuns(c);
            if (runs.length) {
              out.push(new Paragraph({ children: runs, alignment: AlignmentType.JUSTIFIED, spacing: { after: 120 } }));
            }
          } else if (tag === 'table') {
            flushPending();
            const trList = [];
            Array.from(c.querySelectorAll ? c.querySelectorAll('tr') : []).forEach((tr) => {
              const cellEls = Array.from(tr.children).filter((el) => {
                const tn = el.tagName.toLowerCase();
                return tn === 'td' || tn === 'th';
              });
              if (!cellEls.length) return;
              const headerRow = cellEls[0].tagName.toLowerCase() === 'th';
              const cells = cellEls.map((cell) => {
                const isHeader = cell.tagName.toLowerCase() === 'th';
                let runs = isHeader
                  ? [new TextRun({ text: cell.textContent || '', bold: true })]
                  : inlineRuns(cell);
                if (!runs.length) runs = [new TextRun({ text: cell.textContent || '' })];
                return new TableCell({
                  children: [new Paragraph({ children: runs })],
                  margins: { top: 60, bottom: 60, left: 100, right: 100 },
                  shading: isHeader ? { fill: 'ECE9FF' } : undefined,
                });
              });
              trList.push(new TableRow({ children: cells, tableHeader: headerRow }));
            });
            if (trList.length) {
              out.push(new Table({ rows: trList, width: { size: 100, type: WidthType.PERCENTAGE } }));
            }
          } else if (tag === 'ul' || tag === 'ol') {
            flushPending();
            c.childNodes.forEach((li) => {
              if (li.nodeType !== Node.ELEMENT_NODE || li.tagName.toLowerCase() !== 'li') return;
              const runs = inlineRuns(li);
              out.push(new Paragraph({
                children: runs.length ? runs : [new TextRun({ text: li.textContent || '' })],
                bullet: tag === 'ul' ? { level: 0 } : undefined,
                numbering: tag === 'ol' ? { reference: 'default-numbering', level: 0 } : undefined,
                indent: { left: 360 },
                spacing: { after: 60 },
              }));
            });
          } else if (tag === 'br') {
            // Inline : reste dans le paragraphe courant comme soft break.
            // Deux <br> consécutifs (= \n\n du markdown) → on flush pour créer
            // un nouveau paragraphe.
            const last = pending[pending.length - 1];
            if (last && last._isBr) {
              pending.pop();
              flushPending();
            } else {
              pending.push(mkBreak());
            }
          } else if (tag === 'a' || tag === 'span' || tag === 'strong' || tag === 'b'
                  || tag === 'em' || tag === 'i' || tag === 'u' || tag === 'sup'
                  || tag === 'code') {
            // Inline : accumule dans le paragraphe courant.
            const runs = inlineRuns(c);
            if (runs.length) pending.push(...runs);
            else if (c.textContent) pending.push(new TextRun({ text: c.textContent }));
          } else {
            // Bloc inconnu : recurse
            walk(c);
          }
        });
      };

      walk(root);
      flushPending();
      if (!out.length) out.push(new Paragraph(''));
      return out;
    },

    // ============ UTILS ============
    formatTime(dt) {
      if (!dt) return '';
      try {
        const d = new Date(dt);
        return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      } catch (e) { return ''; }
    },

    scrollToBottom() {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    },

    escapeHtml(str) {
      const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
      return String(str || '').replace(/[&<>"']/g, m => map[m]);
    },

    copySynthese(msg) {
      const tmp = document.createElement('div');
      tmp.innerHTML = msg.syntheseHtml || '';
      const txt = tmp.textContent || tmp.innerText || '';
      if (navigator.clipboard) {
        navigator.clipboard.writeText(txt).catch(() => {});
      }
    },
  },
};
</script>

<style>
/* ========== AGENT PAGE — scoped under .agent-root ========== */
.agent-root {
  /* Design tokens */
  --brand: #5b56f0;
  --brand-soft: #ece9ff;
  --brand-softer: #f5f3ff;
  --brand-ink: #3e3ab5;
  --bg: oklch(98.5% 0.005 280);
  --bg-elev: #ffffff;
  --bg-sidebar: oklch(97% 0.005 280);
  --bg-sunken: oklch(96% 0.005 280);
  --border: oklch(92% 0.008 280);
  --border-strong: oklch(86% 0.01 280);
  --text: oklch(22% 0.01 280);
  --text-muted: oklch(48% 0.01 280);
  --text-dim: oklch(62% 0.008 280);
  --green: oklch(62% 0.13 150);
  --green-soft: oklch(93% 0.05 150);
  --rose: oklch(62% 0.13 20);
  --amber: oklch(72% 0.13 75);
  --violet: oklch(62% 0.13 300);
  --indigo: oklch(58% 0.15 275);
  --radius: 10px;
  --radius-lg: 14px;
  --radius-xl: 20px;
  --shadow-sm: 0 1px 2px oklch(20% 0.01 280 / 0.06);
  --shadow: 0 2px 8px oklch(20% 0.01 280 / 0.06), 0 1px 2px oklch(20% 0.01 280 / 0.08);
  --shadow-lg: 0 12px 32px oklch(20% 0.01 280 / 0.12), 0 2px 4px oklch(20% 0.01 280 / 0.08);

  color: var(--text);
  background: var(--bg);
  font-size: 14px;
  line-height: 1.5;
}

.agent-root .app {
  display: grid;
  grid-template-columns: 1fr;
  min-height: calc(100vh - 70px);
}
.agent-root .app.no-sidebar { grid-template-columns: 1fr; }

/* ========== CONVERSATION ========== */
.agent-root .conversation {
  display: flex; flex-direction: column;
  min-width: 0;
  background: var(--bg);
}
.agent-root .dossier-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 14px 28px; border-bottom: 1px solid var(--border);
  background: rgba(255,255,255,0.85); backdrop-filter: blur(8px);
  position: sticky; top: 0; z-index: 10;
}
.agent-root .dh-left { display: flex; align-items: center; gap: 14px; }
.agent-root .dh-kicker { font-size: 11px; color: var(--text-dim); text-transform: uppercase; letter-spacing: 0.04em; font-weight: 500; }
.agent-root .dh-title { font-size: 15px; font-weight: 600; line-height: 1.3; }
.agent-root .dh-right { display: flex; gap: 8px; }

.agent-root .icon-btn {
  width: 32px; height: 32px; border-radius: 7px;
  background: none; border: none;
  display: flex; align-items: center; justify-content: center;
  color: var(--text-muted); transition: all .12s;
  cursor: pointer;
}
.agent-root .icon-btn:hover { background: var(--bg-sunken); color: var(--text); }

.agent-root .btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 12px; border-radius: 7px; font-size: 13px; font-weight: 500;
  border: 1px solid transparent; transition: all .12s;
  cursor: pointer;
  font-family: inherit;
  text-decoration: none;
}
.agent-root .btn-ghost { background: none; color: var(--text); border-color: var(--border); }
.agent-root .btn-ghost:hover { background: var(--bg-sunken); border-color: var(--border-strong); }
.agent-root .btn-primary { background: var(--brand); color: white; }
.agent-root .btn-primary:hover { background: var(--brand-ink); }
.agent-root .btn-word {
  background-color: #2563eb;
  color: white;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.4);
  font-weight: 600;
}
.agent-root .btn-word:hover:not(:disabled) {
  background-color: #1d4ed8;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.5);
}
.agent-root .btn-word:disabled {
  background-color: #94a3b8;
  box-shadow: none;
  cursor: not-allowed;
  opacity: 0.85;
}

/* ========== THREAD ========== */
.agent-root .thread {
  flex: 1 1 auto;
  scroll-behavior: smooth;
}
.agent-root .thread-inner {
  max-width: 820px; margin: 0 auto;
  padding: 32px 28px 40px;
  display: flex; flex-direction: column; gap: 32px;
}

/* ========== MESSAGE: USER ========== */
.agent-root .msg-user { display: flex; flex-direction: column; gap: 0; }
.agent-root .msg-user .msg-bubble {
  max-width: 100%;
  padding: 14px 22px;
  background: white;
  border: 1px solid var(--border);
  color: #0f0f14;
  border-radius: 12px;
  font-size: 15px; line-height: 1.5;
  font-weight: 450;
  white-space: pre-wrap;
}
.agent-root .msg-meta {
  display: flex; align-items: center; gap: 8px;
  font-size: 11.5px; color: var(--text-dim);
}

/* ========== MESSAGE: AGENT ========== */
.agent-root .msg-agent { display: flex; flex-direction: column; gap: 10px; }
.agent-root .a-avatar {
  width: 22px; height: 22px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  overflow: hidden;
}
.agent-root .a-avatar img {
  width: 100%; height: 100%; object-fit: cover; display: block;
}
.agent-root .agent-body { display: flex; flex-direction: column; gap: 18px; }

/* ========== LIVE TIMELINE (style sobre, gris — calqué version-beta) ========== */
.agent-root .live-timeline {
  background: transparent;
  border: none;
  border-radius: 0;
  overflow: hidden;
}
.agent-root .live-timeline.streaming { border-color: var(--border-strong); }
.agent-root .lt-header {
  width: 100%;
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 12px;
  background: var(--brand-softer);
  border: 1px solid var(--border);
  border-radius: 10px;
  color: var(--text);
  cursor: pointer;
  font-family: inherit;
  transition: background .12s, border-color .12s;
}
.agent-root .lt-header:hover {
  background: var(--brand-soft);
  border-color: var(--border-strong);
}
.agent-root .lt-left { display: flex; align-items: center; gap: 12px; }
.agent-root .agent-badge {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px; border-radius: 999px;
  font-size: 12px; font-weight: 500;
  background: var(--bg-sunken);
  color: var(--text-muted);
}
.agent-root .agent-badge.streaming {
  background: var(--brand-soft);
  color: var(--brand-ink);
}
.agent-root .agent-badge.done {
  background: var(--brand-softer);
  color: var(--brand-ink);
}
.agent-root .agent-badge.failed {
  background: #fef2f2;
  color: #b91c1c;
}
.agent-root .pulse-dot {
  width: 7px; height: 7px; border-radius: 50%; background: var(--brand);
  animation: agent-pulse 1.4s ease-in-out infinite;
}
@keyframes agent-pulse {
  0%,100% { opacity: 1; transform: scale(1); }
  50% { opacity: .5; transform: scale(0.75); }
}
.agent-root .lt-meta {
  font-size: 12px; color: var(--text-muted);
  display: inline-flex; align-items: center; gap: 6px;
}

.agent-root .lt-body {
  border-top: none;
  padding: 4px 4px 8px;
}

.agent-root .tl-step {
  display: flex; gap: 12px;
  padding: 8px 0;
  position: relative;
}
.agent-root .tl-rail { display: flex; flex-direction: column; align-items: center; flex-shrink: 0; }
.agent-root .tl-bubble {
  width: 18px; height: 18px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: var(--text-muted); font-size: 9px; font-weight: 600;
  background: var(--bg-sunken);
  border: 1px solid var(--border-strong);
  z-index: 1; flex-shrink: 0;
}
.agent-root .tl-bubble.tl-reasoning {
  background: var(--brand-soft);
  border-color: var(--brand-soft);
  color: var(--brand-ink);
}
.agent-root .tl-bubble.tl-search,
.agent-root .tl-bubble.tl-doc,
.agent-root .tl-bubble.tl-success,
.agent-root .tl-bubble.tl-writing {
  background: var(--bg-sunken);
  color: var(--text-muted);
}
.agent-root .tl-bubble svg { width: 10px; height: 10px; }
.agent-root .tl-bubble.pulsing {
  box-shadow: 0 0 0 0 rgba(91, 86, 240, 0.35);
  animation: agent-ring 1.4s ease-out infinite;
}
@keyframes agent-ring {
  0% { box-shadow: 0 0 0 0 rgba(91, 86, 240, 0.35); }
  100% { box-shadow: 0 0 0 8px rgba(91, 86, 240, 0); }
}
.agent-root .tl-line {
  flex: 1; width: 1.5px;
  background: var(--brand-soft);
  margin-top: 2px;
}
.agent-root .tl-body-text { flex: 1; padding-bottom: 4px; padding-top: 3px; min-width: 0; }
.agent-root .tl-head {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  font-size: 13px;
}
.agent-root .tl-head-success strong { color: var(--text); font-weight: 550; }
.agent-root .tl-text.tl-doc-ref {
  color: var(--text);
  font-weight: 500;
  font-size: 12.5px;
  font-style: normal;
}
.agent-root .tl-error { color: var(--rose); }
.agent-root .tl-tag {
  font-size: 10.5px; background: var(--bg-sunken);
  color: var(--text-muted); padding: 1px 7px;
  border-radius: 999px; font-weight: 500;
}
.agent-root .tl-text {
  font-size: 12.5px; color: var(--text-muted);
  margin-top: 4px; line-height: 1.55;
  padding-right: 12px;
}
.agent-root .tl-mono { font-family: inherit; font-size: 11.5px; color: var(--text-muted); font-style: italic; }
.agent-root .tl-source-chip {
  font-size: 10.5px; padding: 2px 7px;
  background: var(--bg-sunken); border: 1px solid var(--border);
  border-radius: 4px; color: var(--text-muted);
  font-family: inherit;
}
.agent-root .tl-iter {
  font-size: 10.5px; padding: 1px 7px;
  background: var(--brand-softer); border: 1px solid var(--brand-soft);
  border-radius: 999px; color: var(--brand-ink);
  font-weight: 500;
  font-family: inherit;
}
.agent-root .tl-step-reasoning .tl-head strong { color: var(--brand-ink); }
.agent-root .tl-step-reasoning .tl-text { color: #2d2b6b; }
.agent-root .tl-reasoning-content {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.agent-root .tl-reasoning-content p { margin: 0; padding: 0; }

/* ========== SYNTHESE (box de réponse) ========== */
.agent-root .synthese {
  background: #ffffff;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}
.agent-root .synthese-body {
  padding: 22px 26px 26px;
  font-family: inherit;
  font-size: 15px; line-height: 2.3;
  color: var(--text);
}
.agent-root .synthese-body p { margin: 0 0 14px; }
.agent-root .synthese-body p:last-child { margin-bottom: 0; }
.agent-root .synthese-body h1,
.agent-root .synthese-body h2,
.agent-root .synthese-body h3,
.agent-root .synthese-body h4,
.agent-root .synthese-body h5,
.agent-root .synthese-body h6 {
  font-family: inherit;
  font-size: 13px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--brand-ink);
  margin: 28px 0 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--brand-soft);
}
.agent-root .synthese-body ul,
.agent-root .synthese-body ol {
  padding-left: 22px; margin: 0 0 14px;
}
.agent-root .synthese-body li { margin-bottom: 6px; }
.agent-root .synthese-body strong { font-weight: 600; color: oklch(15% 0.01 280); }

/* Tableaux générés par l'IA (markdown GFM → marked) */
.agent-root .synthese-body .synthese-table-wrap {
  margin: 18px 0;
  overflow-x: auto;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  -webkit-overflow-scrolling: touch;
}
.agent-root .synthese-body table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  line-height: 1.55;
  background: #fff;
}
.agent-root .synthese-body thead th {
  background: var(--brand-soft);
  color: var(--brand-ink);
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
}
.agent-root .synthese-body th,
.agent-root .synthese-body td {
  padding: 10px 14px;
  border: 1px solid var(--border);
  text-align: left;
  vertical-align: top;
}
.agent-root .synthese-body tbody tr:nth-child(even) {
  background: var(--brand-softer);
}
.agent-root .synthese-body tbody tr:hover {
  background: var(--brand-soft);
}

/* Liens web sortants (query_web) — violet brand, soulignés au hover */
.agent-root .synthese-body a.web-link {
  color: var(--brand);
  text-decoration: none;
  border-bottom: 1px solid color-mix(in oklch, var(--brand) 30%, transparent);
  transition: border-color .15s, color .15s;
}
.agent-root .synthese-body a.web-link:hover {
  border-bottom-color: var(--brand);
  text-decoration: none;
}

/* Citations = hyperliens inline avec le titre du document (mêmes règles que DossierPage) */
.agent-root .synthese-body .synthesis-reference {
  color: var(--brand);
  font-style: italic;
  text-decoration: underline;
  cursor: pointer;
  padding: 0 2px;
  border-radius: 2px;
  font-size: 0.92em;
  background: none;
  border: none;
  transition: color .2s, background-color .2s;
}
.agent-root .synthese-body .synthesis-reference:hover {
  color: var(--brand-ink);
  background-color: rgba(99, 102, 241, 0.1);
}

.agent-root .synthese-body .workspace-citation {
  display: inline;
  position: relative;
  color: #6c63ff;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  padding: 0 2px;
  border-radius: 3px;
  transition: color 0.15s ease, background 0.15s ease;
}
.agent-root .synthese-body .workspace-citation:hover {
  color: #5a52d5;
  background: #ede9fe;
  text-decoration: none;
}
/* Tooltip instantané : nom du fichier + pages au survol */
.agent-root .synthese-body .workspace-citation:hover::after {
  content: attr(data-ws-tip);
  position: absolute;
  bottom: calc(100% + 7px);
  left: 50%;
  transform: translateX(-50%);
  width: max-content;
  max-width: 320px;
  padding: 5px 10px;
  background: #1a1a2e;
  color: #fff;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.4;
  white-space: normal;
  text-align: center;
  z-index: 60;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}
.agent-root .synthese-body .inactive-reference {
  color: #6b7280;
  font-style: italic;
  text-decoration: underline dotted;
}
.agent-root .synthese-body .official-link-icon {
  margin-left: 4px;
  color: var(--brand);
  opacity: 0.7;
  vertical-align: middle;
  text-decoration: none;
  font-size: 0.85em;
}
.agent-root .synthese-body .official-link-icon:hover { opacity: 1; }

/* ========== MESSAGE ACTIONS ========== */
.agent-root .msg-actions-row {
  display: flex; align-items: center; gap: 2px;
  padding: 2px 0;
}
.agent-root .btn-ghost-sm {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 10px; border-radius: 6px;
  background: none; border: none;
  font-size: 12px; color: var(--text-muted);
  transition: all .12s;
  cursor: pointer;
  font-family: inherit;
}
.agent-root .btn-ghost-sm:hover { background: var(--bg-sunken); color: var(--text); }

/* Bouton Word per-turn — même style bleu que .btn-word mais plus petit. */
.agent-root .btn-word-sm {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 10px; border-radius: 6px;
  background-color: #2563eb;
  color: white;
  border: none;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all .12s;
  box-shadow: 0 1px 3px rgba(37, 99, 235, 0.35);
}
.agent-root .btn-word-sm:hover:not(:disabled) {
  background-color: #1d4ed8;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.45);
}
.agent-root .btn-word-sm:disabled {
  background-color: #94a3b8;
  box-shadow: none;
  cursor: not-allowed;
  opacity: 0.85;
}
.agent-root .btn-word-sm .spinner-icon {
  animation: spin 1s linear infinite;
}

/* Bouton Mémo per-turn — même gabarit que .btn-word-sm, couleurs mémo (violet). */
.agent-root .btn-memo-sm {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 10px; border-radius: 6px;
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  color: #625CFB;
  border: none;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all .12s;
}
.agent-root .btn-memo-sm:hover {
  background: linear-gradient(135deg, #e0e7ff, #c7d2fe);
  box-shadow: 0 2px 6px rgba(98, 92, 251, 0.25);
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* ========== SOURCES SHELF ========== */
.agent-root .sources-toggle {
  width: 100%;
  display: flex; align-items: center; gap: 10px;
  padding: 12px 16px;
  background: white;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  font-size: 13px; color: var(--text);
  text-align: left;
  transition: all .12s;
  cursor: pointer;
  font-family: inherit;
}
.agent-root .sources-toggle:hover { border-color: var(--border-strong); background: var(--bg-sunken); }
.agent-root .sources-toggle strong { font-weight: 600; }
.agent-root .st-dot { width: 3px; height: 3px; border-radius: 50%; background: var(--text-dim); }
.agent-root .st-hint { color: var(--text-muted); font-size: 12px; }

.agent-root .sources-shelf {
  background: white;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;
}
.agent-root .sources-head {
  padding: 14px 18px;
  border-bottom: 1px solid var(--border);
  display: flex; justify-content: space-between; align-items: center;
}
.agent-root .sh-title { display: flex; align-items: center; gap: 8px; font-size: 13.5px; }
.agent-root .sh-hint { font-size: 11.5px; color: var(--text-dim); }

.agent-root .sources-nav {
  padding: 12px 18px;
  border-bottom: 1px solid var(--border);
  background: var(--bg-sunken);
}
.agent-root .src-chips-flat { display: flex; flex-wrap: wrap; gap: 5px; }

.agent-root .src-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px; border-radius: 999px;
  background: white;
  border: 1px solid var(--border);
  font-size: 12px; color: var(--text);
  font-weight: 500;
  transition: all .12s;
  cursor: pointer;
  font-family: inherit;
}
.agent-root .src-chip:hover { border-color: var(--brand); }
.agent-root .src-chip.active { background: var(--brand); border-color: var(--brand); color: white; }
.agent-root .src-count {
  font-size: 10.5px; padding: 1px 6px;
  background: var(--bg-sunken); color: var(--text-muted);
  border-radius: 999px; font-weight: 600;
}
.agent-root .src-chip.active .src-count { background: rgba(255,255,255,0.2); color: white; }

.agent-root .sources-list { max-height: 480px; overflow-y: auto; }
.agent-root .source-row {
  width: 100%; text-align: left;
  display: grid; grid-template-columns: 28px 1fr auto;
  gap: 12px;
  padding: 14px 18px;
  background: none; border: none;
  border-bottom: 1px solid var(--border);
  transition: background .12s;
  cursor: pointer;
  font-family: inherit;
}
.agent-root .source-row:hover { background: var(--bg-sunken); }
.agent-root .source-row:last-child { border-bottom: none; }
.agent-root .sr-num {
  width: 24px; height: 24px; border-radius: 6px;
  background: var(--brand-soft); color: var(--brand-ink);
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 600;
}
.agent-root .sr-title { font-size: 13.5px; font-weight: 500; color: var(--text); line-height: 1.35; }
.agent-root .sr-sub { font-size: 11.5px; color: var(--text-dim); margin-top: 2px; }
.agent-root .sr-summary {
  font-size: 12.5px; color: var(--text-muted);
  margin-top: 6px; line-height: 1.5;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden;
}
.agent-root .sr-score {
  font-size: 11.5px; color: var(--text-muted); font-weight: 500;
  text-align: right; padding-top: 2px;
}
.agent-root .sr-score-num { color: var(--green); font-weight: 600; font-size: 13px; }
.agent-root .sr-score-max { opacity: .6; }

/* ========== STREAMING PREVIEW ========== */
.agent-root .streaming-preview {
  background: white;
  border: 1px dashed var(--border-strong);
  border-radius: var(--radius-lg);
  padding: 20px;
}
.agent-root .sp-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.agent-root .sp-card {
  padding: 14px; border-radius: 8px;
  background: var(--bg-sunken);
  display: flex; flex-direction: column; gap: 8px;
}
.agent-root .sp-card-line {
  height: 8px; border-radius: 4px;
  background: linear-gradient(90deg, var(--border), var(--border-strong), var(--border));
  background-size: 200% 100%;
  animation: agent-shimmer 1.4s ease-in-out infinite;
}
.agent-root .sp-card-line.w80 { width: 80%; }
.agent-root .sp-card-line.w60 { width: 60%; }
.agent-root .sp-card-line.w40 { width: 40%; }
@keyframes agent-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.agent-root .sp-hint {
  margin-top: 14px; text-align: center;
  font-size: 12px; color: var(--text-dim); font-style: italic;
}

/* ========== FAILED BLOCK ========== */
.agent-root .agent-failed-block {
  margin-top: 16px;
  padding: 16px 18px;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.agent-root .agent-failed-msg {
  margin: 0;
  font-size: 14px;
  color: #991b1b;
}
.agent-root .agent-failed-detail {
  font-size: 13px;
  color: #b91c1c;
}
.agent-root .btn-retry {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 8px;
  background: #b91c1c;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
}
.agent-root .btn-retry:hover:not(:disabled) { background: #991b1b; }
.agent-root .btn-retry:disabled { opacity: 0.6; cursor: not-allowed; }

/* ========== COMPOSER (TransformModal style) ========== */
.agent-root .composer {
  padding: 12px 28px 20px;
  position: sticky; bottom: 0; z-index: 10;
  background: var(--bg);
}

/* ========== PANNEAU DOCUMENT (mode rédaction) ========== */
.agent-root .redaction-doc-panel {
  display: flex; align-items: center; gap: 10px;
  max-width: 820px;
  margin: 0 auto 10px;
  padding: 10px 14px;
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  box-sizing: border-box;
}
.agent-root .redaction-doc-panel .rdp-icon {
  flex-shrink: 0;
  width: 32px; height: 32px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 8px;
  background: var(--brand-soft);
  color: var(--brand);
}
.agent-root .redaction-doc-panel .rdp-info { flex: 1; min-width: 0; }
.agent-root .redaction-doc-panel .rdp-name {
  font-size: 13px; font-weight: 600; color: var(--text);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.agent-root .redaction-doc-panel .rdp-sub {
  font-size: 11.5px; color: var(--text-muted);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.agent-root .redaction-doc-panel .rdp-download { flex-shrink: 0; }
.agent-root .composer .transform-search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  min-height: 56px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 9999px;
  max-width: 820px;
  margin: 0 auto;
  box-sizing: border-box;
}
.agent-root .composer .transform-search-bar-multiline {
  flex-direction: column;
  align-items: stretch;
  padding: 12px 12px 12px;
  min-height: auto;
  border-radius: 16px;
  transition: none;
}
.agent-root .composer .transform-search-bar-multiline .transform-search-input {
  min-height: 48px;
  max-height: 180px;
  padding: 6px 0;
  flex: 0 0 auto;
}
.agent-root .composer .transform-search-bar-multiline .transform-send-circle {
  align-self: flex-end;
}
.agent-root .composer .transform-search-input {
  flex: 1;
  min-width: 0;
  min-height: 24px;
  max-height: 400px;
  padding: 16px 8px;
  margin: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 15px;
  line-height: 24px;
  resize: none;
  overflow-y: auto;
  color: #000;
  font-family: inherit;
  transition: none;
}
.agent-root .composer .transform-search-input:focus {
  outline: none;
  box-shadow: none;
}
.agent-root .composer .transform-search-input::placeholder {
  color: #9e9e9e;
}
.agent-root .composer .transform-send-circle {
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  padding: 0;
  background: #625CFB;
  color: #fff;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(98, 92, 251, 0.35);
}
.agent-root .composer .transform-send-circle:hover:not(:disabled) {
  background: #5147e5;
  box-shadow: 0 4px 12px rgba(98, 92, 251, 0.45);
  transform: translateY(-1px);
}
.agent-root .composer .transform-send-circle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.agent-root .composer .transform-send-circle .arrow-icon {
  stroke-width: 2.5;
  stroke-linecap: round;
}
.agent-root .spinner-icon {
  animation: agent-spin 1s linear infinite;
}
@keyframes agent-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}


/* ========== LOADING / ERROR ========== */
.agent-root .loading-screen {
  display: flex; align-items: center; justify-content: center;
  height: 100%;
  color: var(--text-muted);
  font-size: 14px;
}
.agent-root .loading-screen.error { color: var(--rose); }

/* ========== SCROLLBARS ========== */
.agent-root .thread::-webkit-scrollbar,
.agent-root .dossier-list::-webkit-scrollbar,
.agent-root .sources-list::-webkit-scrollbar,
.agent-root .dp-body::-webkit-scrollbar { width: 10px; }
.agent-root .thread::-webkit-scrollbar-thumb,
.agent-root .dossier-list::-webkit-scrollbar-thumb,
.agent-root .sources-list::-webkit-scrollbar-thumb,
.agent-root .dp-body::-webkit-scrollbar-thumb {
  background: var(--border-strong); border-radius: 5px;
  border: 3px solid transparent; background-clip: padding-box;
}
</style>
